import React, { useState, createContext, useContext, useEffect, ReactNode, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform, useSpring, useInView, cubicBezier } from 'framer-motion';
import logo from './logo.png';

// ==================== ТИПЫ И ПЕРЕВОДЫ ====================
type Language = 'ru' | 'en';

const translations: Record<Language, Record<string, string>> = {
  ru: {
    'nav.about': 'О компании',
    'nav.product': 'Продукты',
    'nav.invest': 'Инвестиции',
    'nav.partners': 'Партнеры',
    'nav.contacts': 'Контакты',
    'nav.button': 'Записаться на консультацию',
    'hero.badge': 'IT-компания нового поколения',
    'hero.title': 'MARQELLON SOFTWARE DESIGN',
    'hero.subtitle': 'Мы автоматизируем бизнес, снижаем издержки и ускоряем рост — через ИИ, цифровые платформы и лицензированные IT-решения.',
    'hero.call': 'Позвонить',
    'hero.wa': 'WhatsApp',
    'hero.explore': 'Узнать больше',
    'hero.cta.main': 'Получить бесплатный аудит',
    'hero.cta.sub': 'Консультация за 15 минут — без обязательств',
    'cta.title': 'Готовы сократить издержки бизнеса на 30%?',
    'cta.subtitle': 'Наши эксперты проведут бесплатный IT-аудит вашего бизнеса и покажут, где теряются деньги.',
    'cta.button': 'ПОЛУЧИТЬ БЕСПЛАТНЫЙ АУДИТ',
    'cta.name': 'Ваше имя',
    'cta.phone': 'Телефон',
    'cta.email': 'Email',
    'cta.guarantee': '🔒 Ваши данные в безопасности. Мы работаем по договору NDA.',
    'about.title': 'О компании',
    'about.subtitle': 'Общая информация и профиль деятельности',
    'about.description': 'Компания является IT-организацией, осуществляющей деятельность по:',
    'about.activities.dev': 'Разработке программного обеспечения',
    'about.activities.platform': 'Внедрению цифровых платформ',
    'about.activities.support': 'Сопровождению IT-решений',
    'about.activities.license': 'Лицензированию собственных программных продуктов',
    'about.activities.invest': 'Привлечению инвестиций для развития технологических проектов',
    'about.disclaimer': 'Компания не осуществляет торговую деятельность, не продаёт товары конечным покупателям и не является платёжной организацией',
    'product.findrive.title': 'FinDrive',
    'product.findrive.short': 'Экосистема для водителей и автопарков: мгновенные выплаты, AI-анализ вождения, 24/7 поддержка.',
    'product.findrive.full': '**FinDrive** – цифровая экосистема для водителей и автопарков.\n**Ключевые возможности:**\n• Мгновенные выплаты заработка (FinPay)\n• AI-анализ стиля вождения (Driver Safety Index)\n• Аналитика для таксопарков\n• SOS-помощь и техподдержка 24/7\n• Интеграция с IoT-оборудованием (OBD-II, GPS)\n• Финансовый модуль с учетом налогов\n**Результат:** Повышение безопасности, прозрачности расчетов и снижение операционных издержек.',
    'product.finlogistics.title': 'FinLogistics',
    'product.finlogistics.short': 'AI-платформа управления складом, товарными потоками и прогнозирования спроса.',
    'product.finlogistics.full': '**FinLogistics** – AI-платформа интеллектуального управления складом и капиталом.\n**Ключевые возможности:**\n• Единый учет складов и остатков\n• AI-прогнозирование спроса (1–12 месяцев)\n• ABC/XYZ-анализ и маржинальность\n• Оптимизация распределения товаров\n• Динамическое ценообразование\n• Предиктивная логистика и контроль потерь\n• Финансовая аналитика оборотного капитала\n**Результат:** Снижение издержек хранения, рост оборачиваемости и повышение рентабельности.',
    'product.finsystems.title': 'FinSystems (ЕИП)',
    'product.finsystems.short': 'Единое информационное пространство: интеграция данных, мониторинг инфраструктуры.',
    'product.finsystems.full': '**FinSystems** – цифровая платформа интеграции данных и мониторинга инфраструктуры.\n**Ключевые возможности:**\n• Централизованная база данных и документооборот\n• Мониторинг сетевой инфраструктуры\n• Аналитика стабильности и диагностика сбоев\n• Совместная работа сотрудников\n• BI-отчётность и контроль кибербезопасности\n**Результат:** Повышение прозрачности процессов и стабильности IT-инфраструктуры.',
    'product.finschool.title': 'FinSchool',
    'product.finschool.short': 'Образовательная платформа с видеосвязью, аналитикой и геймификацией.',
    'product.finschool.full': '**FinSchool** – интеллектуальная образовательная платформа онлайн-обучения.\n**Ключевые возможности:**\n• Онлайн-уроки и вебинары (WebRTC)\n• Трекинг прогресса и успеваемости\n• База знаний и медиатека\n• Геймификация обучения\n• AI-анализ рисков отсева\n**Результат:** Полная цифровизация образовательного процесса до 10 000+ пользователей.',
    'product.goldaxis.title': 'GoldAxisPlatform',
    'product.goldaxis.short': 'B2B/B2C-платформа для сделок на рынке золота с цифровым доверием.',
    'product.goldaxis.full': '**GoldAxisPlatform** – цифровая B2B/B2C-платформа для сделок на рынке золота.\n**Ключевые возможности:**\n• AI-проверка участников\n• Цифровой документооборот\n• Прозрачный протокол сделки\n• Независимый механизм обеспечения обязательств\n• Отсутствие хранения средств платформой\n**Результат:** Технологический слой доверия в традиционно офлайн-рынке.',
    'products.title': 'Программные продукты',
    'products.description': 'Решения, которые меняют бизнес',
    'products.revenue': 'Доход формируется от:',
    'products.rev1': 'Лицензирования ПО',
    'products.rev2': 'Интеграции и внедрения',
    'products.rev3': 'Подписки на платформы',
    'products.rev4': 'Технического сопровождения',
    'invest.title': 'Инвестиционная деятельность',
    'invest.text': 'Компания обладает лицензией на инвестиционную деятельность в рамках развития собственных технологических проектов.',
    'crypto.title': 'Цифровые активы',
    'crypto.text': 'Используем криптовалюту для международных расчетов с подрядчиками и оплаты зарубежных лицензий.',
    'partners.title': 'Наши партнеры',
    'partners.subtitle': 'Мы сотрудничаем с ведущими компаниями Казахстана',
    'partners.visit': 'Перейти на сайт',
    'footer.copyright': '© 2026 Marqellon Software Design',
    'footer.garcom': 'Сайт сделан в Garcom',
    'footer.allrights': 'Все права защищены',
    'footer.company': 'Marqellon Software Design',
    'product.modal.close': 'Закрыть',
    'product.modal.more': 'Подробнее',
    'modal.title': 'Оставить заявку',
    'modal.subtitle': 'Наш менеджер свяжется с вами в течение 15 минут.',
    'modal.name': 'Ваше имя',
    'modal.phone': 'Телефон',
    'modal.email': 'Email',
    'modal.send': 'ПОЛУЧИТЬ БЕСПЛАТНЫЙ АУДИТ',
    'contacts.title': 'Контакты',
    'contacts.map.open': 'Открыть в Google Maps',
    'contacts.address': 'Dubai, IFZA Business Park, DDP',
    'contacts.phone': '+971 (561) 366-651',
    'contacts.email': 'info@marqellon.com',
    'quote.text': '«Agent-Based Financial Model»',
    'quote.subtitle': 'Мы зарабатываем только когда вы экономите. Комиссия признается исключительно от оптимизированных затрат вашего бизнеса.',
    'bottom.title': 'MARQELLON SOFTWARE DESIGN',
    'bottom.description': 'Мы — команда инженеров и аналитиков. Компания не является торговой организацией и не продаёт товары конечным покупателям. Наша специализация — комплексные IT-системы и лицензирование программного обеспечения.',
    'dashboard.title': 'Панель управления',
    'dashboard.deployments': 'Активные развертывания',
    'dashboard.capacity': 'Мощность',
    'dashboard.finance': 'Финансы',
    'dashboard.licenses': 'Последние лицензии',
    'dashboard.annual': 'Годовая',
    'dashboard.quarterly': 'Квартальная',
    'dashboard.biennial': 'Двухгодичная',
    'mobile.call': 'Позвонить',
    'mobile.whatsapp': 'WhatsApp',
    'desktop.call': 'Call',
    'desktop.whatsapp': 'WhatsApp',
    'stats.products': 'Продуктов',
    'stats.platforms': 'Платформ',
    'stats.support': 'Поддержка',
    'partners.general': 'Генеральный партнер',
    'partners.legal': 'Юридическая поддержка транзакций, корпоративное право и юридическое сопровождение бизнес-процессов.',
    'partners.tag1': 'Корпоративное право',
    'partners.tag2': 'Поддержка транзакций',
    'partners.tag3': 'Юридический консалтинг',
    'invest.item1.ru': '• ПРИВЛЕЧЕНИЕ КАПИТАЛА',
    'invest.item2.ru': '• ЛИЦЕНЗИРОВАННОЕ УПРАВЛЕНИЕ',
    'invest.item3.ru': '• ТЕХНОЛОГИЧЕСКИЙ РОСТ',
    'invest.item1.en': '• CAPITAL ATTRACTION',
    'invest.item2.en': '• LICENSED MANAGEMENT',
    'invest.item3.en': '• TECHNOLOGY GROWTH',
    'crypto.item1.ru': '• B2B РАСЧЕТЫ',
    'crypto.item2.ru': '• ОПЛАТА ЛИЦЕНЗИЙ ПО',
    'crypto.item3.ru': '• МЕЖДУНАРОДНЫЕ ПЕРЕВОДЫ',
    'crypto.item1.en': '• B2B SETTLEMENTS',
    'crypto.item2.en': '• SOFTWARE LICENSE PAYMENTS',
    'crypto.item3.en': '• INTERNATIONAL TRANSIT',
    'why.title': 'Почему Marqellon',
    'why.sub': 'Стандарты уровня ОАЭ. Качество без компромиссов.',
    'why.1.title': 'Зарегистрированы в ОАЭ',
    'why.1.text': 'IFZA Dubai — юрисдикция доверия для международного бизнеса',
    'why.2.title': 'Лицензированная деятельность',
    'why.2.text': 'Полное соответствие законодательству ОАЭ и международным стандартам',
    'why.3.title': 'AI-первый подход',
    'why.3.text': 'Каждое решение основано на искусственном интеллекте и данных',
    'why.4.title': 'Прозрачные условия',
    'why.4.text': 'Никаких скрытых комиссий — вы платите только за результат',
    'why.5.title': 'Экспертиза 5+ лет',
    'why.5.text': 'Глубокая экспертиза в финтех, логистике и образовании',
    'why.6.title': 'Поддержка 24/7',
    'why.6.text': 'Наша команда всегда на связи, в любом часовом поясе',
    'numbers.title': 'Marqellon в цифрах',
    'numbers.sub': 'Реальные результаты. Проверенный рост.',
    'numbers.1.val': '5+',
    'numbers.1.label': 'Продуктовых платформ',
    'numbers.2.val': '$722K',
    'numbers.2.label': 'Финансовый оборот',
    'numbers.3.val': '63.8%',
    'numbers.3.label': 'Рост год-к-году',
    'numbers.4.val': '24/7',
    'numbers.4.label': 'Техническая поддержка',
    'numbers.5.val': 'UAE',
    'numbers.5.label': 'IFZA Business Park',
    'numbers.6.val': '2026',
    'numbers.6.label': 'Год основания',
    'process.title': 'Как мы работаем',
    'process.sub': 'От идеи до результата — прозрачно и быстро',
    'process.1.title': 'Аудит и анализ',
    'process.1.text': 'Бесплатная диагностика текущей IT-инфраструктуры и бизнес-процессов. Выявляем узкие места за 48 часов.',
    'process.2.title': 'Стратегия и архитектура',
    'process.2.text': 'Проектируем оптимальное решение. Показываем ROI до начала разработки — вы знаете результат заранее.',
    'process.3.title': 'Разработка и интеграция',
    'process.3.text': 'Agile-команда, спринты по 2 недели. Вы видите прогресс каждый день, не в конце проекта.',
    'process.4.title': 'Запуск и сопровождение',
    'process.4.text': 'Плавный запуск без простоев. Поддержка 24/7, SLA-гарантии, обновления включены.',
    'cases.title': 'Результаты клиентов',
    'cases.sub': 'Цифры, которые говорят сами за себя',
    'cases.1.company': 'Таксопарк, 200+ машин',
    'cases.1.result': '-38% операционных расходов',
    'cases.1.text': 'Внедрили FinDrive. Автоматизировали выплаты водителям, AI-мониторинг вождения снизил аварийность на 22%.',
    'cases.2.company': 'Дистрибьютор, 15 складов',
    'cases.2.result': '+41% оборачиваемость товара',
    'cases.2.text': 'FinLogistics с AI-прогнозированием спроса. Сократили товарные остатки на $200K за первый квартал.',
    'cases.3.company': 'Образовательный центр',
    'cases.3.result': '10,000+ студентов онлайн',
    'cases.3.text': 'FinSchool масштабировала офлайн-школу до онлайн-платформы. Доход вырос в 3 раза за 8 месяцев.',
    'faq.title': 'Частые вопросы',
    'faq.sub': 'Ответы на главные вопросы',
    'faq.1.q': 'Сколько стоит разработка?',
    'faq.1.a': 'Стоимость зависит от масштаба проекта. Мы работаем по модели подписки на лицензию — вы не платите огромную сумму сразу. Минимальный порог входа: от $2,000/месяц. Первая консультация и аудит — бесплатно.',
    'faq.2.q': 'Как быстро получим первые результаты?',
    'faq.2.a': 'Первые измеримые результаты — в течение 30 дней после старта. Полное внедрение и автоматизация: 2-4 месяца. Мы работаем итерационно — вы видите прогресс каждые 2 недели.',
    'faq.3.q': 'Вы работаете только с крупным бизнесом?',
    'faq.3.a': 'Нет. Наши платформы масштабируются под любой бизнес: от 10 до 10,000+ пользователей. Стартапы, средний бизнес и корпорации — у нас есть решение для каждого.',
    'faq.4.q': 'Как обстоит дело с безопасностью данных?',
    'faq.4.a': 'Все данные хранятся на защищённых серверах с шифрованием. Мы соответствуем международным стандартам безопасности. Ваши данные никогда не передаются третьим лицам.',
    'faq.5.q': 'Нужна ли нам IT-команда для работы с вашими платформами?',
    'faq.5.a': 'Нет. Наши платформы созданы для бизнес-пользователей. Обучение занимает 1-2 дня. Техническая поддержка 24/7 всегда на связи.',
    'mission.label': 'О компании',
    'mission.title': 'Наша миссия',
    'mission.tagline': 'Технологии, которые работают на ваш рост',
    'mission.p1': 'Marqellon Software Design — это IT-компания нового поколения, основанная в 2026 году в Дубае. Мы созданы для одной цели: помочь бизнесу расти быстрее с помощью искусственного интеллекта и цифровых платформ.',
    'mission.p2': 'Наш подход отличается от традиционного IT-аутсорсинга: мы не просто разрабатываем программы — мы встраиваемся в ваш бизнес, анализируем потери и строим системы, которые окупаются с первого месяца.',
    'mission.p3': 'Зарегистрированы в IFZA Dubai. Работаем на рынках ОАЭ и СНГ.',
    'quote.pullquote': 'Мы строим не просто программы — мы строим системы, которые делают ваш бизнес умнее с каждым днём.',
    'quote.author': '— Команда Marqellon',
  },
  en: {
    'nav.about': 'About',
    'nav.product': 'Products',
    'nav.invest': 'Investments',
    'nav.partners': 'Partners',
    'nav.contacts': 'Contacts',
    'nav.button': 'Book a Consultation',
    'hero.badge': 'Next-Generation IT Company',
    'hero.title': 'MARQELLON SOFTWARE DESIGN',
    'hero.subtitle': 'We automate business, reduce costs and accelerate growth — through AI, digital platforms and licensed IT solutions.',
    'hero.call': 'Call Now',
    'hero.wa': 'WhatsApp',
    'hero.explore': 'Explore More',
    'hero.cta.main': 'Get Free IT Audit',
    'hero.cta.sub': '15-minute consultation — no commitment',
    'cta.title': 'Ready to cut business costs by 30%?',
    'cta.subtitle': 'Our experts will conduct a free IT audit and show where your money is leaking.',
    'cta.button': 'GET FREE AUDIT',
    'cta.name': 'Your name',
    'cta.phone': 'Phone',
    'cta.email': 'Email',
    'cta.guarantee': '🔒 Your data is safe. We work under NDA.',
    'about.title': 'About Company',
    'about.subtitle': 'General Information and Activity Profile',
    'about.description': 'The company is an IT organization engaged in:',
    'about.activities.dev': 'Software Development',
    'about.activities.platform': 'Digital Platform Implementation',
    'about.activities.support': 'IT Solutions Support',
    'about.activities.license': 'Licensing of Own Software Products',
    'about.activities.invest': 'Attracting Investments for Technology Projects',
    'about.disclaimer': 'The company does not engage in trade, does not sell products to end customers, and is not a payment organization',
    'product.findrive.title': 'FinDrive',
    'product.findrive.short': 'Ecosystem for drivers and fleets: instant payments, AI driving analysis, 24/7 support.',
    'product.findrive.full': '**FinDrive** – a digital ecosystem for drivers and fleets.\n**Key features:**\n• Instant earnings (FinPay)\n• AI driving style analysis (Driver Safety Index)\n• Fleet analytics\n• 24/7 SOS and support\n• IoT integration (OBD-II, GPS)\n• Tax-aware financial module\n**Result:** Increased safety, transparent settlements, reduced operating costs.',
    'product.finlogistics.title': 'FinLogistics',
    'product.finlogistics.short': 'AI platform for warehouse management, demand forecasting, and capital optimization.',
    'product.finlogistics.full': '**FinLogistics** – an AI platform for intelligent warehouse and capital management.\n**Key features:**\n• Unified warehouse and stock accounting\n• AI demand forecasting (1–12 months)\n• ABC/XYZ and margin analysis\n• Optimal product distribution\n• Dynamic pricing\n• Predictive logistics and loss control\n• Working capital analytics\n**Result:** Reduced storage costs, increased turnover, improved profitability.',
    'product.finsystems.title': 'FinSystems (UIS)',
    'product.finsystems.short': 'Unified information space: data integration, infrastructure monitoring.',
    'product.finsystems.full': '**FinSystems** – a digital platform for data integration and infrastructure monitoring.\n**Key features:**\n• Centralized database and document flow\n• Network infrastructure monitoring\n• Stability analytics and fault diagnostics\n• Employee collaboration tools\n• BI reporting and cybersecurity control\n**Result:** Increased process transparency and IT infrastructure stability.',
    'product.finschool.title': 'FinSchool',
    'product.finschool.short': 'Educational platform with video calls, analytics, and gamification.',
    'product.finschool.full': '**FinSchool** – an intelligent educational platform for online learning.\n**Key features:**\n• Online lessons and webinars (WebRTC)\n• Progress and performance tracking\n• Knowledge base and media library\n• Gamification\n• AI-based dropout risk analysis\n**Result:** Full digitalization of education, scalability to 10,000+ users.',
    'product.goldaxis.title': 'GoldAxisPlatform',
    'product.goldaxis.short': 'B2B/B2C platform for gold market deals with digital trust layer.',
    'product.goldaxis.full': '**GoldAxisPlatform** – a next-gen digital platform for gold market transactions.\n**Key features:**\n• AI participant verification\n• Digital document flow\n• Transparent deal protocol\n• Independent escrow-like mechanism\n• No platform custody of funds\n**Result:** Building a technology trust layer in a traditionally offline market.',
    'products.title': 'Software Products',
    'products.description': 'Solutions That Change Business',
    'products.revenue': 'Revenue from:',
    'products.rev1': 'Software Licensing',
    'products.rev2': 'Integration & Implementation',
    'products.rev3': 'Platform Subscriptions',
    'products.rev4': 'Technical Support',
    'invest.title': 'Investment Activity',
    'invest.text': 'The company is licensed for investment activities to develop its own technological projects.',
    'crypto.title': 'Digital Assets',
    'crypto.text': 'We use cryptocurrency for international settlements and software licensing.',
    'partners.title': 'Our Partners',
    'partners.subtitle': 'We cooperate with leading companies in Kazakhstan',
    'partners.visit': 'Visit Website',
    'footer.copyright': '© 2026 Marqellon Software Design',
    'footer.garcom': 'Website made by Garcom',
    'footer.allrights': 'All rights reserved',
    'footer.company': 'Marqellon Software Design',
    'product.modal.close': 'Close',
    'product.modal.more': 'More details',
    'modal.title': 'Submit Request',
    'modal.subtitle': 'Our manager will contact you within 15 minutes.',
    'modal.name': 'Your name',
    'modal.phone': 'Phone',
    'modal.email': 'Email',
    'modal.send': 'GET FREE AUDIT',
    'contacts.title': 'Contacts',
    'contacts.map.open': 'Open in Google Maps',
    'contacts.address': 'Dubai, IFZA Business Park, DDP',
    'contacts.phone': '+971 (561) 366-651',
    'contacts.email': 'info@marqellon.com',
    'quote.text': '"Agent-Based Financial Model"',
    'quote.subtitle': 'We earn only when you save. Commission is recognized exclusively from the optimized costs of your business.',
    'bottom.title': 'MARQELLON SOFTWARE DESIGN',
    'bottom.description': 'We are a team of engineers and analysts. The company is not a trading organization and does not sell goods to end customers. Our specialization is complex IT systems and software licensing.',
    'dashboard.title': 'Dashboard',
    'dashboard.deployments': 'Active Deployments',
    'dashboard.capacity': 'Capacity',
    'dashboard.finance': 'Finance',
    'dashboard.licenses': 'Recent Licenses',
    'dashboard.annual': 'Annual',
    'dashboard.quarterly': 'Quarterly',
    'dashboard.biennial': 'Biennial',
    'mobile.call': 'Call',
    'mobile.whatsapp': 'WhatsApp',
    'desktop.call': 'Call',
    'desktop.whatsapp': 'WhatsApp',
    'stats.products': 'Products',
    'stats.platforms': 'Platforms',
    'stats.support': 'Support',
    'partners.general': 'General Partner',
    'partners.legal': 'Legal support for transactions, corporate law and legal support of business processes.',
    'partners.tag1': 'Corporate Law',
    'partners.tag2': 'Transaction Support',
    'partners.tag3': 'Legal Consulting',
    'invest.item1.ru': '• ПРИВЛЕЧЕНИЕ КАПИТАЛА',
    'invest.item2.ru': '• ЛИЦЕНЗИРОВАННОЕ УПРАВЛЕНИЕ',
    'invest.item3.ru': '• ТЕХНОЛОГИЧЕСКИЙ РОСТ',
    'invest.item1.en': '• CAPITAL ATTRACTION',
    'invest.item2.en': '• LICENSED MANAGEMENT',
    'invest.item3.en': '• TECHNOLOGY GROWTH',
    'crypto.item1.ru': '• B2B РАСЧЕТЫ',
    'crypto.item2.ru': '• ОПЛАТА ЛИЦЕНЗИЙ ПО',
    'crypto.item3.ru': '• МЕЖДУНАРОДНЫЕ ПЕРЕВОДЫ',
    'crypto.item1.en': '• B2B SETTLEMENTS',
    'crypto.item2.en': '• SOFTWARE LICENSE PAYMENTS',
    'crypto.item3.en': '• INTERNATIONAL TRANSIT',
    'why.title': 'Why Marqellon',
    'why.sub': 'UAE-level standards. Quality without compromise.',
    'why.1.title': 'Registered in UAE',
    'why.1.text': 'IFZA Dubai — the jurisdiction of trust for international business',
    'why.2.title': 'Licensed Operations',
    'why.2.text': 'Full compliance with UAE law and international standards',
    'why.3.title': 'AI-First Approach',
    'why.3.text': 'Every solution powered by artificial intelligence and data',
    'why.4.title': 'Transparent Terms',
    'why.4.text': 'No hidden fees — you pay only for results',
    'why.5.title': '5+ Years Expertise',
    'why.5.text': 'Deep expertise in fintech, logistics and education',
    'why.6.title': '24/7 Support',
    'why.6.text': 'Our team is always available, across any timezone',
    'numbers.title': 'Marqellon in Numbers',
    'numbers.sub': 'Real results. Proven growth.',
    'numbers.1.val': '5+',
    'numbers.1.label': 'Product Platforms',
    'numbers.2.val': '$722K',
    'numbers.2.label': 'Financial Turnover',
    'numbers.3.val': '63.8%',
    'numbers.3.label': 'Year-on-Year Growth',
    'numbers.4.val': '24/7',
    'numbers.4.label': 'Technical Support',
    'numbers.5.val': 'UAE',
    'numbers.5.label': 'IFZA Business Park',
    'numbers.6.val': '2026',
    'numbers.6.label': 'Year Founded',
    'process.title': 'How We Work',
    'process.sub': 'From idea to result — transparent and fast',
    'process.1.title': 'Audit & Analysis',
    'process.1.text': 'Free diagnosis of your IT infrastructure and business processes. We identify bottlenecks within 48 hours.',
    'process.2.title': 'Strategy & Architecture',
    'process.2.text': 'We design the optimal solution and show ROI before development begins — you know the result in advance.',
    'process.3.title': 'Development & Integration',
    'process.3.text': 'Agile team, 2-week sprints. You see progress every day, not just at the end of the project.',
    'process.4.title': 'Launch & Support',
    'process.4.text': 'Smooth launch with zero downtime. 24/7 support, SLA guarantees, updates included.',
    'cases.title': 'Client Results',
    'cases.sub': 'Numbers that speak for themselves',
    'cases.1.company': 'Taxi fleet, 200+ vehicles',
    'cases.1.result': '-38% operating costs',
    'cases.1.text': 'Deployed FinDrive. Automated driver payouts, AI driving monitoring reduced accidents by 22%.',
    'cases.2.company': 'Distributor, 15 warehouses',
    'cases.2.result': '+41% inventory turnover',
    'cases.2.text': 'FinLogistics with AI demand forecasting. Reduced stock surplus by $200K in the first quarter.',
    'cases.3.company': 'Educational Center',
    'cases.3.result': '10,000+ students online',
    'cases.3.text': 'FinSchool scaled an offline school to an online platform. Revenue grew 3x in 8 months.',
    'faq.title': 'Frequently Asked Questions',
    'faq.sub': 'Answers to the main questions',
    'faq.1.q': 'How much does development cost?',
    'faq.1.a': 'Cost depends on project scale. We work on a license subscription model — no large upfront payment. Minimum entry: from $2,000/month. First consultation and audit are free.',
    'faq.2.q': 'How quickly will we see first results?',
    'faq.2.a': 'First measurable results within 30 days of start. Full implementation and automation: 2-4 months. We work iteratively — you see progress every 2 weeks.',
    'faq.3.q': 'Do you only work with large enterprises?',
    'faq.3.a': 'No. Our platforms scale for any business: from 10 to 10,000+ users. Startups, mid-sized businesses and corporations — we have a solution for everyone.',
    'faq.4.q': 'What about data security?',
    'faq.4.a': 'All data is stored on secure encrypted servers. We comply with international security standards. Your data is never shared with third parties.',
    'faq.5.q': 'Do we need an IT team to use your platforms?',
    'faq.5.a': 'No. Our platforms are built for business users. Training takes 1-2 days. 24/7 technical support is always available.',
    'mission.label': 'About Company',
    'mission.title': 'Our Mission',
    'mission.tagline': 'Technology engineered to accelerate your growth',
    'mission.p1': 'Marqellon Software Design is a next-generation IT company founded in 2026 in Dubai. Built for a single purpose: to help businesses grow faster through artificial intelligence and digital platforms.',
    'mission.p2': 'Our approach differs from traditional IT outsourcing: we don\'t just build software — we embed in your business, analyze your losses and build systems that pay for themselves within the first month.',
    'mission.p3': 'Registered in IFZA Dubai. Operating across UAE and CIS markets.',
    'quote.pullquote': 'We don\'t just build software — we build systems that make your business smarter every single day.',
    'quote.author': '— Marqellon Team',
  }
};

const partnersData = [
  { id: 1, name: 'GFL', url: 'https://gfl.kz/', description: 'General Partner · Legal Support' }
];

// ==================== PALETTE ====================
const champagneGradient = 'linear-gradient(135deg, #A88F5E 0%, #D4C197 50%, #A88F5E 100%)';
const champagneText = {
  background: champagneGradient,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
} as React.CSSProperties;

// ==================== UNSPLASH HELPERS ====================
const UNSPLASH = {
  dubai: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1400&q=80&fit=crop&auto=format',
  dubaiAerial: 'https://images.unsplash.com/photo-1548247416-ec66f4900b2e?w=1400&q=80&fit=crop&auto=format',
  server: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=1200&q=80&fit=crop&auto=format',
  circuit: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=1200&q=80&fit=crop&auto=format',
  aiAbstract: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=1200&q=80&fit=crop&auto=format',
  transport: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=900&q=80&fit=crop&auto=format',
  warehouse: 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=900&q=80&fit=crop&auto=format',
  education: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=900&q=80&fit=crop&auto=format',
  gold: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=1400&q=80&fit=crop&auto=format',
  darkTech: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1600&q=80&fit=crop&auto=format',
  team: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=1400&q=80&fit=crop&auto=format',
  office: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1400&q=80&fit=crop&auto=format',
};

// ==================== CONTEXT ====================
const LanguageContext = createContext<{
  lang: Language;
  setLang: (l: Language) => void;
  t: (key: string) => string;
} | null>(null);

const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [lang, setLang] = useState<Language>('en'); // ENGLISH BY DEFAULT
  const t = (key: string) => translations[lang][key] ?? key;
  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
};

const useLockScroll = (active: boolean) => {
  useEffect(() => {
    if (!active) { document.body.style.cssText = ''; return; }
    const y = window.scrollY;
    document.body.style.cssText = `overflow:hidden;position:fixed;width:100%;top:-${y}px`;
    return () => { document.body.style.cssText = ''; window.scrollTo(0, y); };
  }, [active]);
};

// ==================== ANIMATION VARIANTS ====================
const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.75, ease: cubicBezier(0.16, 1, 0.3, 1) }
  })
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: (i = 0) => ({
    opacity: 1,
    transition: { delay: i * 0.06, duration: 0.65 }
  })
};

// ==================== UI ATOMS ====================
const GoldDivider: React.FC = () => (
  <div className="flex items-center justify-center py-3">
    <div className="h-px flex-1 max-w-[80px]" style={{ background: 'linear-gradient(90deg, transparent, rgba(168,143,94,0.5))' }} />
    <div className="mx-3 flex items-center gap-1.5">
      <div className="w-1 h-1 rounded-full" style={{ background: 'rgba(168,143,94,0.6)' }} />
      <div className="w-1.5 h-1.5 rounded-full border" style={{ borderColor: 'rgba(212,193,151,0.5)' }} />
      <div className="w-1 h-1 rounded-full" style={{ background: 'rgba(168,143,94,0.6)' }} />
    </div>
    <div className="h-px flex-1 max-w-[80px]" style={{ background: 'linear-gradient(90deg, rgba(168,143,94,0.5), transparent)' }} />
  </div>
);

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[11px] font-light uppercase mb-3" style={{ color: 'rgba(168,143,94,0.8)', letterSpacing: '0.25em' }}>
    {children}
  </p>
);

// ==================== VIDEO BACKGROUND ====================
const VideoBackground: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const ua = navigator.userAgent;
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
    const isIOS = /iPad|iPhone|iPod/.test(ua);
    const video = videoRef.current;
    if (!video) return;
    if (isIOS || isSafari) {
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
    }
    video.muted = true;
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => { video.style.display = 'none'; });
    }
  }, []);
  return (
    <video
      ref={videoRef} autoPlay loop muted playsInline preload="metadata"
      className="absolute inset-0 w-full h-full"
      style={{ objectFit: 'cover', objectPosition: 'center center', transform: 'translateZ(0)', willChange: 'transform' }}
      poster="/fon.WebP">
      <source src="/fon.mp4" type="video/mp4" />
    </video>
  );
};

// ==================== GOLDEN FIREFLIES (ЗОЛОТЫЕ ОГОНЬКИ) ====================
const GoldenFireflies: React.FC = () => {
  const isMobile = window.innerWidth < 768;
  const count = isMobile ? 12 : 25; // Уменьшаем на мобильных
  
  const fireflies = Array.from({ length: count }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: isMobile ? Math.random() * 3.5 + 1.5 : Math.random() * 5 + 2,
    delay: Math.random() * 5,
    duration: Math.random() * 8 + 12,
  }));
  
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {fireflies.map(f => (
        <motion.div key={f.id} className="absolute rounded-full"
          style={{ 
            left: `${f.x}%`, 
            top: `${f.y}%`, 
            width: f.size, 
            height: f.size, 
            background: 'radial-gradient(circle, #FFE55C 0%, #FFD700 50%, #D4AF37 100%)',
            boxShadow: `
              0 0 ${f.size * 6}px rgba(255,215,0,0.9),
              0 0 ${f.size * 12}px rgba(255,215,0,0.7),
              0 0 ${f.size * 18}px rgba(212,175,55,0.4),
              0 0 ${f.size * 24}px rgba(255,215,0,0.2),
              inset 0 0 ${f.size * 3}px rgba(255,255,255,0.5)
            `,
            filter: 'blur(0.2px)',
            willChange: 'transform, opacity'
          }}
          animate={{ 
            y: isMobile ? [0, -50, -25, 40, 0] : [0, -80, -40, 60, 0],
            x: isMobile ? [0, 20, -20, 15, 0] : [0, 35, -35, 25, 0],
            opacity: [0.3, 1, 0.8, 0.95, 0.3],
            scale: isMobile ? [0.5, 1.2, 1, 1.2, 0.5] : [0.5, 1.5, 1.1, 1.4, 0.5]
          }}
          transition={{ 
            duration: f.duration, 
            delay: f.delay, 
            repeat: Infinity, 
            ease: 'easeInOut' 
          }} />
      ))}
    </div>
  );
};

// ==================== PARTICLES ====================
const Particles: React.FC = () => {
  const isMobile = window.innerWidth < 768;
  const isTablet = window.innerWidth < 1024;
  
  // Адаптивное количество частиц
  const particleCount = isMobile ? 45 : isTablet ? 80 : 120;
  
  const particles = Array.from({ length: particleCount }, (_, i) => {
    const isLargeParticle = Math.random() > (isMobile ? 0.9 : 0.85);
    return {
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: isLargeParticle 
        ? (isMobile ? Math.random() * 4 + 1.5 : Math.random() * 8 + 3)
        : (isMobile ? Math.random() * 2.5 + 1 : Math.random() * 4.5 + 1.5),
      delay: Math.random() * 8,
      duration: isLargeParticle ? Math.random() * 15 + 25 : Math.random() * 12 + 18,
      opacity: isLargeParticle ? Math.random() * 0.9 + 0.4 : Math.random() * 0.8 + 0.25,
      isLarge: isLargeParticle,
    };
  });
  
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map(p => (
        <motion.div key={p.id} className="absolute rounded-full"
          style={{ 
            left: `${p.x}%`, 
            top: `${p.y}%`, 
            width: p.size, 
            height: p.size, 
            background: p.isLarge 
              ? 'radial-gradient(circle, #FFD700 0%, #D4AF37 40%, #D4C197 100%)'
              : '#D4C197',
            boxShadow: p.isLarge
              ? `
                0 0 ${p.size * 8}px rgba(255,215,0,${p.opacity * 1.2}),
                0 0 ${p.size * 16}px rgba(212,175,55,${p.opacity * 0.8}),
                0 0 ${p.size * 24}px rgba(212,193,151,${p.opacity * 0.5}),
                0 0 ${p.size * 32}px rgba(255,215,0,${p.opacity * 0.25}),
                inset 0 0 ${p.size * 4}px rgba(255,255,255,${p.opacity * 0.3})
              `
              : `
                0 0 ${p.size * 5}px rgba(212,193,151,${p.opacity}),
                0 0 ${p.size * 10}px rgba(212,193,151,${p.opacity * 0.6}),
                0 0 ${p.size * 15}px rgba(168,143,94,${p.opacity * 0.3})
              `,
            filter: p.isLarge ? 'blur(0.2px)' : 'blur(0.5px)',
            willChange: 'transform, opacity'
          }}
          animate={{ 
            y: [0, -60, -20, 40, 0],
            x: [0, 25, -20, 15, 0],
            opacity: [p.opacity * 0.1, p.opacity * 0.9, p.opacity * 0.6, p.opacity * 0.85, p.opacity * 0.15],
            scale: p.isLarge 
              ? [0.5, 1.6, 0.8, 1.4, 0.5]
              : [0.6, 1.4, 0.85, 1.25, 0.6]
          }}
          transition={{ 
            duration: p.duration, 
            delay: p.delay, 
            repeat: Infinity, 
            ease: 'easeInOut' 
          }} />
      ))}
    </div>
  );
};

// ==================== HEADER ====================
const Header: React.FC<{ onOpen: () => void }> = ({ onOpen }) => {
  const { lang, setLang, t } = useLanguage();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useLockScroll(menuOpen);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const navLinks = [
    { href: '#about', label: t('nav.about') },
    { href: '#product', label: t('nav.product') },
    { href: '#process', label: lang === 'ru' ? 'Как работаем' : 'Process' },
    { href: '#invest', label: t('nav.invest') },
    { href: '#partners', label: t('nav.partners') },
    { href: '#contacts', label: t('nav.contacts') },
  ];

  return (
    <>
      <header className="fixed top-0 w-full z-[100] transition-all duration-500"
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          background: scrolled ? 'rgba(4,4,4,0.97)' : 'linear-gradient(180deg, rgba(4,4,4,0.9) 0%, transparent 100%)',
          backdropFilter: scrolled ? 'blur(24px)' : 'blur(8px)',
          borderBottom: scrolled ? '1px solid rgba(168,143,94,0.12)' : '1px solid transparent',
          WebkitBackdropFilter: scrolled ? 'blur(24px)' : 'blur(8px)',
        }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex justify-between items-center">
          <div className="flex items-center gap-2 flex-shrink-0">
            <img src={logo} alt="MARQELLON" className="h-8 sm:h-10 object-contain"
              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          </div>
          <nav className="hidden lg:flex gap-6 xl:gap-8" style={{ fontSize: '10px', letterSpacing: '0.18em' }}>
            {navLinks.map(l => (
              <a key={l.href} href={l.href}
                className="uppercase font-light text-white/55 hover:text-white/90 transition-colors duration-300 relative group py-1">
                {l.label}
                <span className="absolute -bottom-0.5 left-0 w-0 h-px group-hover:w-full transition-all duration-400"
                  style={{ background: 'rgba(212,193,151,0.6)' }} />
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <button onClick={() => setLang(lang === 'ru' ? 'en' : 'ru')}
              className="text-[10px] font-light text-white/55 hover:text-white/80 transition w-10 h-10 flex items-center justify-center rounded-xl"
              style={{ border: '1px solid rgba(168,143,94,0.18)', letterSpacing: '0.12em' }}>
              {lang.toUpperCase()}
            </button>
            <button onClick={onOpen}
              className="hidden sm:flex items-center text-black text-[10px] font-light px-5 py-2.5 rounded-full uppercase transition-all hover:opacity-90 active:scale-95 min-h-[40px]"
              style={{ background: champagneGradient, letterSpacing: '0.15em' }}>
              {t('nav.button')}
            </button>
            <button onClick={() => setMenuOpen(!menuOpen)}
              className="lg:hidden w-10 h-10 flex flex-col justify-center items-center gap-[5px] rounded-xl border flex-shrink-0"
              style={{ background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(168,143,94,0.18)' }}
              aria-label="Menu">
              <span className={`block w-5 h-px rounded-full transition-all duration-300 origin-center ${menuOpen ? 'rotate-45 translate-y-[6px]' : ''}`} style={{ background: 'rgba(212,193,151,0.8)' }} />
              <span className={`block w-5 h-px bg-white/50 rounded-full transition-all duration-300 ${menuOpen ? 'opacity-0 scale-x-0' : ''}`} />
              <span className={`block w-5 h-px rounded-full transition-all duration-300 origin-center ${menuOpen ? '-rotate-45 -translate-y-[6px]' : ''}`} style={{ background: 'rgba(212,193,151,0.8)' }} />
            </button>
          </div>
        </div>
      </header>
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: '100%' }}
            transition={{ type: 'tween', duration: 0.28, ease: [0.25, 0, 0, 1] }}
            className="fixed inset-0 z-[99] flex flex-col"
            style={{
              background: 'linear-gradient(160deg, #060606 0%, #090700 100%)',
              paddingTop: 'calc(env(safe-area-inset-top) + 64px)',
              paddingLeft: 'env(safe-area-inset-left)', paddingRight: 'env(safe-area-inset-right)',
              paddingBottom: 'calc(env(safe-area-inset-bottom) + 16px)',
            }}>
            <nav className="flex flex-col px-6 pt-6 flex-1">
              {navLinks.map((l, i) => (
                <motion.a key={l.href} href={l.href}
                  initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05, duration: 0.3 }}
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center justify-between py-5 border-b font-extralight text-white/75 active:text-white uppercase"
                  style={{ borderColor: 'rgba(168,143,94,0.1)', fontSize: '13px', letterSpacing: '0.18em' }}>
                  {l.label}
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: 'rgba(168,143,94,0.5)' }}>
                    <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </motion.a>
              ))}
            </nav>
            <div className="px-6 mt-8">
              <button onClick={() => { setMenuOpen(false); onOpen(); }}
                className="w-full text-black font-light py-4 rounded-2xl text-sm uppercase tracking-[0.15em] active:scale-95 transition-transform min-h-[56px]"
                style={{ background: champagneGradient }}>
                {t('nav.button')}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// ==================== HERO ====================
const Hero: React.FC<{ onOpen: () => void }> = ({ onOpen }) => {
  const { t, lang } = useLanguage();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 120]);
  const opacity = useTransform(scrollY, [0, 400], [1, 0]);

  return (
    <section className="relative flex items-center justify-center overflow-hidden"
      style={{ minHeight: '100svh', paddingTop: 'calc(env(safe-area-inset-top) + 80px)', paddingBottom: 'calc(env(safe-area-inset-bottom) + 80px)' }}>
      <div className="absolute inset-0 overflow-hidden" style={{ transform: 'translateZ(0)' }}>
        <motion.div className="absolute inset-0" style={{ y }}>
          <VideoBackground />
        </motion.div>
      </div>
      <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(3,3,3,0.75) 0%, rgba(3,3,3,0.45) 40%, rgba(3,3,3,0.95) 100%)' }} />
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 30%, rgba(168,143,94,0.12) 0%, transparent 70%)' }} />
      {/* БОЛЬШИЕ СВЕТЯЩИЕСЯ ОРБЫ */}
      <div className="absolute -top-40 -right-40 md:w-[700px] md:h-[700px] w-[400px] h-[400px] opacity-70 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(255,215,0,0.8) 0%, rgba(212,175,55,0.4) 20%, transparent 65%)',
          filter: 'blur(100px)',
          animation: 'hyper-float 20s ease-in-out infinite'
        }} />
      <div className="absolute -bottom-40 -left-40 md:w-[800px] md:h-[800px] w-[450px] h-[450px] opacity-60 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(212,175,55,0.7) 0%, rgba(212,193,151,0.3) 25%, transparent 65%)',
          filter: 'blur(120px)',
          animation: 'hyper-float 25s ease-in-out infinite reverse'
        }} />
      <div className="absolute top-1/3 -left-20 md:w-[600px] md:h-[600px] w-[350px] h-[350px] opacity-55 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(255,215,0,0.6) 0%, transparent 60%)',
          filter: 'blur(90px)',
          animation: 'hyper-float 18s ease-in-out infinite'
        }} />
      {/* Extra glow orbs for extreme effect */}
      <div className="absolute top-40 right-1/4 md:w-[500px] md:h-[500px] w-[300px] h-[300px] opacity-40 pointer-events-none hidden md:block"
        style={{
          background: 'radial-gradient(circle, rgba(255,215,0,0.5) 0%, transparent 70%)',
          filter: 'blur(110px)',
          animation: 'hyper-float 22s ease-in-out infinite'
        }} />
      <div className="absolute -bottom-20 right-1/3 md:w-[700px] md:h-[700px] w-[400px] h-[400px] opacity-35 pointer-events-none hidden md:block"
        style={{
          background: 'radial-gradient(circle, rgba(212,175,55,0.45) 0%, transparent 65%)',
          filter: 'blur(130px)',
          animation: 'float 28s ease-in-out infinite reverse'
        }} />
      <Particles />
      <GoldenFireflies />
      <motion.div style={{ opacity }} className="relative z-10 text-center px-5 max-w-5xl w-full">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.7 }}
          className="mb-6 inline-flex items-center gap-3 px-5 py-2 rounded-full"
          style={{ border: '1px solid rgba(168,143,94,0.3)', background: 'rgba(168,143,94,0.07)' }}>
          <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: 'rgba(212,193,151,0.9)' }} />
          <span className="font-light uppercase text-white/70" style={{ fontSize: '10px', letterSpacing: '0.25em' }}>{t('hero.badge')}</span>
          <span className="text-[10px] hidden sm:inline font-light text-white/35 uppercase" style={{ letterSpacing: '0.12em' }}>— Dubai, UAE</span>
        </motion.div>
        <motion.h1 initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.22, duration: 0.85 }}
          className="font-extralight leading-[0.92] mb-6"
          style={{ fontSize: 'clamp(2.4rem, 8.5vw, 7rem)', letterSpacing: '-0.03em' }}>
          <span className="text-white/95">MARQELLON</span>
          <br />
          <motion.span 
            initial={{ backgroundPosition: '200% 0' }}
            animate={{ backgroundPosition: '0 0' }}
            transition={{ delay: 0.8, duration: 2 }}
            style={champagneText}
            className="inline-block">SOFTWARE</motion.span>
          <span className="text-white/20"> DESIGN</span>
        </motion.h1>
        <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.36, duration: 0.7 }}
          className="font-light text-white/70 mb-8 max-w-2xl mx-auto leading-relaxed"
          style={{ fontSize: 'clamp(0.95rem, 2.2vw, 1.2rem)', letterSpacing: '0.01em' }}>
          {t('hero.subtitle')}
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.48, duration: 0.6 }}
          className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-8">
          {[
            { value: '5+', label: t('stats.products') },
            { value: '$722K', label: lang === 'ru' ? 'Оборот' : 'Turnover' },
            { value: '24/7', label: t('stats.support') },
            { value: 'UAE', label: 'IFZA Dubai' },
          ].map((s, i) => (
            <motion.div key={i} 
              whileHover={{ scale: 1.12, borderColor: 'rgba(212,193,151,0.8)', backgroundColor: 'rgba(168,143,94,0.15)' }}
              className="flex items-center gap-2 px-4 py-3 rounded-2xl group cursor-pointer transition-all duration-300 relative overflow-hidden"
              style={{ 
                background: 'rgba(168,143,94,0.1)', 
                border: '1.5px solid rgba(168,143,94,0.3)',
                boxShadow: '0 0 20px rgba(168,143,94,0)'
              }}>
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ background: 'radial-gradient(circle at center, rgba(212,193,151,0.2) 0%, transparent 70%)' }} />
              <span className="text-sm font-bold relative z-10 transition-all duration-300 group-hover:scale-125 origin-left" style={champagneText}>{s.value}</span>
              <span className="text-xs text-white/60 font-light relative z-10 transition-colors duration-300 group-hover:text-white/80">{s.label}</span>
            </motion.div>
          ))}
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.6 }}
          className="flex flex-col sm:flex-row gap-3 justify-center items-center">
          <motion.button onClick={onOpen}
            whileHover={{ scale: window.innerWidth < 768 ? 1.05 : 1.15, boxShadow: window.innerWidth < 768 
              ? '0 0 40px rgba(212,193,151,0.8), 0 0 80px rgba(168,143,94,0.5), 0 0 120px rgba(212,193,151,0.2)'
              : '0 0 80px rgba(212,193,151,1), 0 0 150px rgba(168,143,94,0.7), 0 0 200px rgba(212,193,151,0.4), 0 0 300px rgba(212,193,151,0.15)' 
            }}
            whileTap={{ scale: 0.88 }}
            className="w-full sm:w-auto text-black font-bold px-14 py-6 rounded-3xl text-base transition-all active:scale-90 min-h-[60px] uppercase relative overflow-hidden group"
            style={{ 
              background: champagneGradient, 
              letterSpacing: '0.12em',
              boxShadow: '0 0 60px rgba(212,193,151,0.8), 0 0 120px rgba(168,143,94,0.5), 0 0 180px rgba(212,193,151,0.25), inset 0 0 40px rgba(255,255,255,0.15)',
              border: '3px solid rgba(212,193,151,0.6)',
              willChange: 'transform, box-shadow'
            }}>
            {/* Extreme glow layer 1 */}
            <span className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{ 
                background: 'radial-gradient(circle at center, rgba(255,255,255,0.8) 0%, transparent 60%)',
                animation: 'ultra-glow 2s ease-in-out infinite'
              }} />
            {/* Glow layer 2 */}
            <span className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-80 transition-opacity duration-500"
              style={{
                background: 'radial-gradient(ellipse at 30% 30%, rgba(212,193,151,0.6) 0%, transparent 50%)',
                filter: 'blur(8px)'
              }} />
            {/* Shine animation */}
            <span className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100"
              style={{
                background: 'linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.2) 50%, transparent 70%)',
                animation: 'shimmer-intense 2.5s infinite'
              }} />
            <span className="relative font-black drop-shadow-lg">{t('hero.cta.main')}</span>
          </motion.button>
          <div className="flex gap-2.5 w-full sm:w-auto">
            <motion.a href="tel:+971561366651"
              whileHover={{ scale: 1.02, borderColor: 'rgba(212,193,151,0.6)' }}
              className="flex-1 sm:flex-none px-5 py-4 rounded-2xl font-light transition-all flex items-center justify-center gap-2 text-sm min-h-[56px] text-white/75 group cursor-pointer"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(12px)' }}>
              <span className="group-hover:scale-110 transition-transform duration-300">📞</span>
              <span className="hidden sm:inline">{t('hero.call')}</span>
            </motion.a>
            <motion.a href="https://wa.me/971561366651" target="_blank" rel="noreferrer"
              whileHover={{ scale: 1.02, borderColor: 'rgba(212,193,151,0.6)' }}
              className="flex-1 sm:flex-none px-5 py-4 rounded-2xl font-light transition-all flex items-center justify-center gap-2 text-sm min-h-[56px] text-white/75 group cursor-pointer"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(12px)' }}>
              <span className="group-hover:scale-110 transition-transform duration-300">💬</span>
              <span className="hidden sm:inline">{t('hero.wa')}</span>
            </motion.a>
          </div>
        </motion.div>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}
          className="mt-4 text-white/35 text-xs font-light">{t('hero.cta.sub')}</motion.p>
      </motion.div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }}
        className="hidden sm:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2">
        <span className="font-light text-white/40 uppercase" style={{ fontSize: '8px', letterSpacing: '0.3em' }}>Scroll</span>
        <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}
          className="w-px h-7" style={{ background: 'linear-gradient(180deg, rgba(168,143,94,0.6) 0%, transparent 100%)' }} />
      </motion.div>
    </section>
  );
};

// ==================== NUMBERS ====================
const NumbersSection: React.FC = () => {
  const { t } = useLanguage();
  const data = [
    { val: t('numbers.1.val'), label: t('numbers.1.label'), icon: '◈' },
    { val: t('numbers.2.val'), label: t('numbers.2.label'), icon: '◇' },
    { val: t('numbers.3.val'), label: t('numbers.3.label'), icon: '△' },
    { val: t('numbers.4.val'), label: t('numbers.4.label'), icon: '◎' },
    { val: t('numbers.5.val'), label: t('numbers.5.label'), icon: '◉' },
    { val: t('numbers.6.val'), label: t('numbers.6.label'), icon: '◆' },
  ];
  return (
    <section className="py-16 sm:py-24 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #030303 0%, #080700 50%, #030303 100%)' }}>
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(168,143,94,0.25), transparent)' }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }} variants={fadeUp}
          className="text-center mb-12">
          <SectionLabel>{t('numbers.title')}</SectionLabel>
          <GoldDivider />
          <p className="text-white/40 text-xs mt-3 font-light uppercase" style={{ letterSpacing: '0.15em' }}>{t('numbers.sub')}</p>
        </motion.div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px" style={{ background: 'rgba(168,143,94,0.1)' }}>
          {data.map((n, i) => (
            <motion.div key={i} variants={fadeIn} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-30px' }}
              whileHover={{ scale: 1.08, borderColor: 'rgba(212,193,151,0.6)' }}
              className="flex flex-col items-center justify-center py-10 px-4 text-center group hover:bg-white/[0.04] transition-all duration-300 relative cursor-pointer overflow-hidden"
              style={{ 
                background: '#030303', 
                border: '1px solid rgba(168,143,94,0.15)',
                boxShadow: '0 0 0px rgba(168,143,94,0)'
              }}>
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{
                  background: 'radial-gradient(circle at center, rgba(212,193,151,0.25) 0%, rgba(168,143,94,0.1) 30%, transparent 70%)',
                }} />
              <span className="text-[12px] mb-4 text-white/20 group-hover:text-white/60 transition-all duration-500 relative z-10 group-hover:scale-130 origin-center">{n.icon}</span>
              <p className="font-extralight mb-2 relative z-10 transition-transform duration-300 group-hover:scale-110 origin-center" style={{ ...champagneText, fontSize: 'clamp(1.5rem, 3vw, 2.2rem)', letterSpacing: '-0.02em' }}>{n.val}</p>
              <p className="font-light text-white/45 leading-snug text-center relative z-10 transition-colors duration-300 group-hover:text-white/65" style={{ fontSize: '10px', letterSpacing: '0.1em' }}>{n.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ==================== MISSION (NEW SPLIT LAYOUT) ====================
const MissionSection: React.FC = () => {
  const { t } = useLanguage();
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  const activities = [
    t('about.activities.dev'),
    t('about.activities.platform'),
    t('about.activities.support'),
    t('about.activities.license'),
    t('about.activities.invest'),
  ];

  return (
    <section id="about" ref={sectionRef} className="relative overflow-hidden" style={{ background: '#040404' }}>
      {/* Golden glow backgrounds */}
      <div className="absolute -top-20 -left-32 w-[1000px] h-[1000px] opacity-25 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(255,215,0,0.6) 0%, transparent 70%)',
          filter: 'blur(150px)',
          animation: 'hyper-float 35s ease-in-out infinite'
        }} />
      <div className="absolute -bottom-40 right-0 w-[800px] h-[800px] opacity-20 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(212,175,55,0.5) 0%, transparent 70%)',
          filter: 'blur(120px)',
          animation: 'float 30s ease-in-out infinite reverse'
        }} />
      
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(168,143,94,0.18), transparent)' }} />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-2 min-h-[640px]">

          {/* LEFT: Text */}
          <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-16 py-20 sm:py-28 lg:py-36">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
              <SectionLabel>{t('mission.label')}</SectionLabel>
              <h2 className="font-extralight leading-tight mb-2" style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', letterSpacing: '-0.03em', ...champagneText }}>
                {t('mission.title')}
              </h2>
              <p className="font-light text-white/40 mb-8 uppercase" style={{ fontSize: '11px', letterSpacing: '0.2em' }}>
                {t('mission.tagline')}
              </p>
              <GoldDivider />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="mt-6 space-y-4">
              <p className="text-white/70 leading-relaxed font-light" style={{ fontSize: '0.95rem' }}>{t('mission.p1')}</p>
              <p className="text-white/55 leading-relaxed font-light" style={{ fontSize: '0.9rem' }}>{t('mission.p2')}</p>
              <p className="text-white/40 leading-relaxed font-light" style={{ fontSize: '0.85rem' }}>{t('mission.p3')}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="mt-8">
              <p className="text-[9px] font-light text-white/30 mb-3 uppercase" style={{ letterSpacing: '0.22em' }}>
                {t('about.description')}
              </p>
              <div className="space-y-2">
                {activities.map((act, i) => (
                  <motion.div key={i}
                    initial={{ opacity: 0, x: -16 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.6, delay: 0.4 + i * 0.07, ease: [0.16, 1, 0.3, 1] }}
                    className="flex items-center gap-3">
                    <div className="w-1 h-1 rounded-full flex-shrink-0" style={{ background: 'rgba(212,193,151,0.5)' }} />
                    <span className="text-xs text-white/50 font-light">{act}</span>
                  </motion.div>
                ))}
              </div>
              <motion.p
                initial={{ opacity: 0 }}
                animate={isInView ? { opacity: 1 } : {}}
                transition={{ duration: 0.6, delay: 0.9 }}
                className="mt-5 text-[10px] text-white/25 leading-relaxed font-light italic"
                style={{ borderLeft: '1px solid rgba(168,143,94,0.2)', paddingLeft: '12px' }}>
                {t('about.disclaimer')}
              </motion.p>
            </motion.div>
          </div>

          {/* RIGHT: Photo */}
          <motion.div
            initial={{ opacity: 0, scale: 1.04 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="relative lg:min-h-full min-h-[360px] overflow-hidden">
            {/* Photo */}
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${UNSPLASH.office})` }} />
            {/* Dark overlays */}
            <div className="absolute inset-0" style={{ background: 'linear-gradient(270deg, transparent 30%, rgba(4,4,4,0.7) 100%)' }} />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(4,4,4,0.2) 0%, transparent 40%, rgba(4,4,4,0.5) 100%)' }} />
            <div className="absolute inset-0" style={{ background: 'rgba(3,3,3,0.35)', mixBlendMode: 'multiply' }} />
            {/* Champagne color grade */}
            <div className="absolute inset-0 opacity-10" style={{ background: 'radial-gradient(ellipse at 60% 40%, rgba(168,143,94,0.6), transparent 70%)' }} />
            {/* Corner label */}
            <div className="absolute bottom-8 right-8">
              <div className="flex flex-col items-end gap-1">
                <span className="text-[9px] font-light text-white/40 uppercase" style={{ letterSpacing: '0.25em' }}>Dubai, UAE</span>
                <span className="text-[9px] font-light uppercase" style={{ color: 'rgba(168,143,94,0.6)', letterSpacing: '0.2em' }}>IFZA Business Park</span>
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

// ==================== WHY US ====================
const WhySection: React.FC = () => {
  const { t } = useLanguage();
  const items = [
    { title: t('why.1.title'), text: t('why.1.text'), icon: '🇦🇪' },
    { title: t('why.2.title'), text: t('why.2.text'), icon: '✅' },
    { title: t('why.3.title'), text: t('why.3.text'), icon: '🤖' },
    { title: t('why.4.title'), text: t('why.4.text'), icon: '💎' },
    { title: t('why.5.title'), text: t('why.5.text'), icon: '📈' },
    { title: t('why.6.title'), text: t('why.6.text'), icon: '🌐' },
  ];
  return (
    <section className="py-20 sm:py-28 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #040404 0%, #060500 100%)' }}>
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(168,143,94,0.18), transparent)' }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }}
          className="text-center mb-14">
          <SectionLabel>{t('why.title')}</SectionLabel>
          <h2 className="font-extralight text-white/90 leading-tight mb-3" style={{ fontSize: 'clamp(1.7rem, 5vw, 3.5rem)', letterSpacing: '-0.025em' }}>
            {t('why.sub')}
          </h2>
          <GoldDivider />
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {items.map((item, i) => (
            <motion.div key={i} variants={fadeUp} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-30px' }}
              whileHover={{ scale: 1.12, borderColor: 'rgba(212,193,151,0.7)', y: -12 }}
              className="group p-7 rounded-3xl transition-all duration-300 relative overflow-hidden cursor-pointer"
              style={{ 
                background: 'linear-gradient(135deg, rgba(212,193,151,0.08) 0%, rgba(168,143,94,0.03) 100%)',
                border: '2px solid rgba(212,193,151,0.25)',
                boxShadow: '0 0 25px rgba(168,143,94,0.15), 0 8px 32px rgba(0,0,0,0.2)'
              }}>
              {/* Animated glow overlay */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{ background: 'radial-gradient(ellipse at 30% 20%, rgba(212,193,151,0.3) 0%, transparent 65%)', animation: 'glow-pulse 3s ease-in-out infinite' }} />
              
              {/* Border glow effect */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 rounded-3xl transition-opacity duration-300 pointer-events-none"
                style={{ 
                  border: '1px solid',
                  borderImage: 'linear-gradient(135deg, rgba(212,193,151,0.6) 0%, transparent 100%) 1'
                }} />
              
              <div className="text-4xl mb-5 transition-transform duration-300 group-hover:scale-140 relative z-10 origin-top-left">{item.icon}</div>
              <h3 className="font-light text-white/95 mb-3 text-sm uppercase transition-all duration-300 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-yellow-100 group-hover:to-yellow-50 group-hover:bg-clip-text relative z-10" style={{ letterSpacing: '0.08em' }}>{item.title}</h3>
              <p className="text-white/60 text-sm leading-relaxed font-light transition-colors duration-300 group-hover:text-white/85 relative z-10">{item.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ==================== PROCESS ====================
const ProcessSection: React.FC = () => {
  const { t } = useLanguage();
  const steps = [
    { num: '01', title: t('process.1.title'), text: t('process.1.text'), icon: '🔍' },
    { num: '02', title: t('process.2.title'), text: t('process.2.text'), icon: '🗺️' },
    { num: '03', title: t('process.3.title'), text: t('process.3.text'), icon: '⚙️' },
    { num: '04', title: t('process.4.title'), text: t('process.4.text'), icon: '🚀' },
  ];
  return (
    <section id="process" className="py-20 sm:py-28 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #030303 0%, #070600 100%)' }}>
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(168,143,94,0.2), transparent)' }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }}
          className="text-center mb-14">
          <SectionLabel>{t('process.title')}</SectionLabel>
          <h2 className="font-extralight text-white/90 leading-tight mb-3" style={{ fontSize: 'clamp(1.7rem, 5vw, 3.5rem)', letterSpacing: '-0.025em' }}>
            {t('process.sub')}
          </h2>
          <GoldDivider />
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {steps.map((step, i) => (
            <motion.div key={i} variants={fadeUp} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-30px' }}
              whileHover={{ scale: 1.11, borderColor: 'rgba(212,193,151,0.6)', y: -10 }}
              className="relative p-7 sm:p-8 rounded-3xl group overflow-hidden transition-all duration-300 cursor-pointer"
              style={{ 
                background: 'linear-gradient(135deg, rgba(212,193,151,0.1) 0%, rgba(168,143,94,0.04) 100%)',
                border: '2px solid rgba(212,193,151,0.2)',
                boxShadow: '0 0 20px rgba(168,143,94,0.12), 0 8px 32px rgba(0,0,0,0.25)'
              }}>
              {/* Animated glow */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{ background: 'radial-gradient(ellipse at 40% 30%, rgba(212,193,151,0.25) 0%, transparent 65%)', animation: 'glow-pulse 3.5s ease-in-out infinite' }} />
              
              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-px z-10 group-hover:w-10 transition-all duration-300"
                  style={{ background: 'linear-gradient(90deg, rgba(212,193,151,0.6) 0%, transparent 100%)', boxShadow: '0 0 15px rgba(212,193,151,0.4)' }} />
              )}
              <div className="flex items-center gap-4 mb-5 relative z-10">
                <span className="text-4xl transition-transform duration-300 group-hover:scale-150 origin-center">{step.icon}</span>
                <span className="font-extralight text-4xl transition-all duration-300 group-hover:scale-125 group-hover:text-yellow-200 origin-left" style={{ ...champagneText, letterSpacing: '-0.03em' }}>{step.num}</span>
              </div>
              <h3 className="font-light text-white/95 text-sm uppercase mb-4 transition-all duration-300 group-hover:text-yellow-100 relative z-10" style={{ letterSpacing: '0.08em' }}>{step.title}</h3>
              <p className="text-white/60 text-sm leading-relaxed font-light transition-colors duration-300 group-hover:text-white/80 relative z-10">{step.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ==================== PRODUCTS (BENTO GRID) ====================
const ProductModal: React.FC<{ isOpen: boolean; onClose: () => void; productKey: string }> = ({ isOpen, onClose, productKey }) => {
  const { t } = useLanguage();
  useLockScroll(isOpen);
  if (!productKey) return null;
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)', paddingLeft: 'env(safe-area-inset-left)', paddingRight: 'env(safe-area-inset-right)' }}
          onClick={onClose}>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0" style={{ background: 'rgba(3,3,3,0.95)', backdropFilter: 'blur(32px)' }} />
          <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 34, stiffness: 260 }}
            className="relative w-full sm:max-w-2xl sm:rounded-[2rem] rounded-t-[2rem] flex flex-col"
            style={{ background: 'rgba(10,8,3,0.97)', border: '1px solid rgba(168,143,94,0.18)', maxHeight: '90svh' }}
            onClick={e => e.stopPropagation()}>
            <div className="h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(168,143,94,0.6), transparent)' }} />
            <div className="flex justify-center pt-3 pb-0 sm:hidden flex-shrink-0">
              <div className="w-10 h-1 rounded-full" style={{ background: 'rgba(168,143,94,0.25)' }} />
            </div>
            <div className="flex items-start justify-between gap-3 pt-5 sm:pt-8 px-6 sm:px-8 pb-4 border-b flex-shrink-0"
              style={{ borderColor: 'rgba(168,143,94,0.1)' }}>
              <h3 className="font-extralight leading-tight text-white/90" style={{ fontSize: 'clamp(1.2rem, 3vw, 1.75rem)', letterSpacing: '-0.01em' }}>
                {t(`product.${productKey}.title`)}
              </h3>
              <button onClick={onClose}
                className="w-9 h-9 flex items-center justify-center rounded-xl text-white/50 hover:text-white/80 text-xl leading-none flex-shrink-0 transition-colors"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>×</button>
            </div>
            <div className="overflow-y-auto px-6 sm:px-8 py-5 flex-1">
              <div className="text-white/65 leading-relaxed space-y-2">
                {t(`product.${productKey}.full`).split('\n').map((line, i) => {
                  if (line.startsWith('**') && line.endsWith('**'))
                    return <p key={i} className="text-sm sm:text-base font-light text-white/90 mt-4 first:mt-0 uppercase" style={{ letterSpacing: '0.06em' }}>{line.slice(2, -2)}</p>;
                  if (line.trim() === '') return <div key={i} className="h-1" />;
                  return <p key={i} className="text-sm font-light">{line}</p>;
                })}
              </div>
            </div>
            <div className="px-6 sm:px-8 py-5 border-t flex-shrink-0" style={{ borderColor: 'rgba(168,143,94,0.1)' }}>
              <button onClick={onClose}
                className="w-full sm:w-auto text-black font-medium px-8 py-3.5 rounded-xl text-sm min-h-[48px] active:scale-95 transition-transform uppercase"
                style={{ background: champagneGradient, letterSpacing: '0.12em' }}>
                {t('product.modal.close')}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

const ProductsSection: React.FC<{ onProductClick: (key: string) => void }> = ({ onProductClick }) => {
  const { t } = useLanguage();

  type ProductItem = {
    key: string;
    icon: string;
    title: string;
    short: string;
    image: string;
    accent: string;
    span?: string;
  };

  const productItems: ProductItem[] = [
    {
      key: 'findrive', icon: '🚗',
      title: t('product.findrive.title'),
      short: t('product.findrive.short'),
      image: UNSPLASH.transport,
      accent: 'rgba(168,143,94,0.15)',
      span: 'lg:col-span-2',
    },
    {
      key: 'finlogistics', icon: '📦',
      title: t('product.finlogistics.title'),
      short: t('product.finlogistics.short'),
      image: UNSPLASH.warehouse,
      accent: 'rgba(74,222,128,0.08)',
    },
    {
      key: 'finsystems', icon: '🖥️',
      title: t('product.finsystems.title'),
      short: t('product.finsystems.short'),
      image: UNSPLASH.server,
      accent: 'rgba(99,179,237,0.08)',
    },
    {
      key: 'finschool', icon: '🎓',
      title: t('product.finschool.title'),
      short: t('product.finschool.short'),
      image: UNSPLASH.education,
      accent: 'rgba(167,139,250,0.08)',
    },
    {
      key: 'goldaxis', icon: '🏅',
      title: t('product.goldaxis.title'),
      short: t('product.goldaxis.short'),
      image: UNSPLASH.gold,
      accent: 'rgba(168,143,94,0.12)',
      span: 'lg:col-span-2',
    },
  ];

  const revenue = [t('products.rev1'), t('products.rev2'), t('products.rev3'), t('products.rev4')];

  return (
    <section id="product" className="py-20 sm:py-28 lg:py-36 overflow-hidden relative" style={{ background: '#040404' }}>
      {/* Golden glow backgrounds */}
      <div className="absolute -top-32 -right-40 w-[1200px] h-[1200px] opacity-30 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(255,215,0,0.7) 0%, transparent 70%)',
          filter: 'blur(160px)',
          animation: 'hyper-float 40s ease-in-out infinite'
        }} />
      <div className="absolute bottom-0 left-1/4 w-[800px] h-[800px] opacity-25 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(212,175,55,0.5) 0%, transparent 70%)',
          filter: 'blur(120px)',
          animation: 'float 35s ease-in-out infinite reverse'
        }} />
      
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(168,143,94,0.18), transparent)' }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }}
          className="text-center mb-14">
          <SectionLabel>{t('products.title')}</SectionLabel>
          <h2 className="font-extralight text-white/90 leading-tight mb-3" style={{ fontSize: 'clamp(1.7rem, 5vw, 3.5rem)', letterSpacing: '-0.025em' }}>
            {t('products.description')}
          </h2>
          <GoldDivider />
        </motion.div>

        {/* BENTO GRID */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {productItems.map((item, i) => (
            <motion.div
              key={item.key}
              variants={fadeUp} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-30px' }}
              onClick={() => onProductClick(item.key)}
              className={`group cursor-pointer relative rounded-3xl overflow-hidden min-h-[260px] sm:min-h-[300px] ${item.span || ''}`}
              style={{ 
                border: '2px solid rgba(212,193,151,0.2)',
                boxShadow: '0 0 30px rgba(168,143,94,0.2), 0 8px 32px rgba(0,0,0,0.25)'
              }}
              whileHover={{ scale: 1.08, borderColor: 'rgba(212,193,151,0.7)', boxShadow: '0 0 40px rgba(212,193,151,0.35), 0 0 80px rgba(168,143,94,0.2)' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}>

              {/* Background image */}
              <div
                className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-125"
                style={{ backgroundImage: `url(${item.image})` }} />

              {/* Gradient overlays */}
              <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(3,3,3,0.45) 0%, rgba(3,3,3,0.82) 60%, rgba(3,3,3,0.97) 100%)' }} />
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{ background: 'linear-gradient(135deg, rgba(212,193,151,0.2) 0%, transparent 50%)', animation: 'glow-pulse 3s ease-in-out infinite' }} />
              <div className="absolute inset-0" style={{ background: item.accent }} />

              {/* Top accent line */}
              <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(212,193,151,0.6), transparent)' }} />

              {/* Icon chip */}
              <motion.div className="absolute top-5 left-5 w-12 h-12 rounded-xl flex items-center justify-center text-xl group-hover:scale-150 transition-all duration-300 relative overflow-hidden"
                style={{ 
                  background: 'rgba(3,3,3,0.9)', 
                  border: '2px solid rgba(212,193,151,0.5)', 
                  backdropFilter: 'blur(12px)',
                  boxShadow: '0 0 30px rgba(212,193,151,0.4), 0 0 60px rgba(212,193,151,0.15)'
                }}>
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ background: 'radial-gradient(circle, rgba(212,193,151,0.4) 0%, transparent 70%)' }} />
                <span className="relative z-10">{item.icon}</span>
              </motion.div>

              {/* Content */}
              <div className="absolute inset-0 flex flex-col justify-end p-6">
                <h3 className="font-light text-white/95 mb-1.5 uppercase transition-all duration-300 group-hover:text-white group-hover:scale-105 origin-left" style={{ fontSize: item.span ? 'clamp(1rem, 2vw, 1.25rem)' : '0.9rem', letterSpacing: '0.1em' }}>
                  {item.title}
                </h3>
                <p className="text-white/50 text-xs leading-relaxed font-light mb-4 line-clamp-2 transition-colors duration-300 group-hover:text-white/70">{item.short}</p>
                <div className="flex items-center gap-1.5 text-xs font-light group-hover:text-white/90 transition-colors duration-300" style={champagneText}>
                  <span className="uppercase" style={{ letterSpacing: '0.1em', fontSize: '10px' }}>{t('product.modal.more')}</span>
                  <motion.svg
                    className="w-3 h-3"
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    animate={{ x: 0 }}
                    whileHover={{ x: 6 }}
                    transition={{ type: 'spring', stiffness: 400, damping: 17 }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
                  </motion.svg>
                </div>
              </div>

              {/* Hover glow - улучшенный */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{ background: 'radial-gradient(ellipse at 50% 100%, rgba(212,193,151,0.2), transparent 50%)' }} />
            </motion.div>
          ))}
        </div>

        {/* Revenue Tags */}
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="mt-8 flex flex-wrap gap-2 justify-center">
          <span className="text-[9px] font-light text-white/30 self-center uppercase mr-2" style={{ letterSpacing: '0.2em' }}>{t('products.revenue')}</span>
          {revenue.map((item, i) => (
            <motion.span key={i} 
              whileHover={{ scale: 1.1, borderColor: 'rgba(168,143,94,0.4)', backgroundColor: 'rgba(168,143,94,0.1)' }}
              className="px-3 py-1.5 rounded-lg text-[10px] font-light transition-all cursor-pointer"
              style={{ background: 'rgba(168,143,94,0.05)', border: '1px solid rgba(168,143,94,0.12)', color: 'rgba(255,255,255,0.45)', letterSpacing: '0.05em' }}>
              {item}
            </motion.span>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// ==================== CASES (WITH BG IMAGES) ====================
const CasesSection: React.FC<{ onOpen: () => void }> = ({ onOpen }) => {
  const { t } = useLanguage();

  type CaseItem = {
    company: string;
    result: string;
    text: string;
    image: string;
    accentColor: string;
  };

  const cases: CaseItem[] = [
    {
      company: t('cases.1.company'),
      result: t('cases.1.result'),
      text: t('cases.1.text'),
      image: UNSPLASH.transport,
      accentColor: 'rgba(168,143,94,0.2)',
    },
    {
      company: t('cases.2.company'),
      result: t('cases.2.result'),
      text: t('cases.2.text'),
      image: UNSPLASH.warehouse,
      accentColor: 'rgba(74,222,128,0.1)',
    },
    {
      company: t('cases.3.company'),
      result: t('cases.3.result'),
      text: t('cases.3.text'),
      image: UNSPLASH.education,
      accentColor: 'rgba(99,179,237,0.1)',
    },
  ];

  return (
    <section className="py-20 sm:py-28 relative overflow-hidden" style={{ background: '#030303' }}>
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(168,143,94,0.18), transparent)' }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }}
          className="text-center mb-14">
          <SectionLabel>{t('cases.title')}</SectionLabel>
          <h2 className="font-extralight text-white/90 leading-tight mb-3" style={{ fontSize: 'clamp(1.7rem, 5vw, 3.5rem)', letterSpacing: '-0.025em' }}>
            {t('cases.sub')}
          </h2>
          <GoldDivider />
        </motion.div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
          {cases.map((c, i) => (
            <motion.div key={i}
              variants={fadeUp} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-30px' }}
              className="group relative rounded-3xl overflow-hidden min-h-[380px] cursor-default"
              style={{ border: '1px solid rgba(168,143,94,0.12)' }}>

              {/* BG image - hidden under dark, reveals on hover */}
              <div
                className="absolute inset-0 bg-cover bg-center transition-all duration-700 group-hover:scale-105"
                style={{ backgroundImage: `url(${c.image})`, filter: 'grayscale(40%)' }} />

              {/* Opaque dark overlay that partially lifts on hover */}
              <div className="absolute inset-0 transition-opacity duration-700"
                style={{ background: 'linear-gradient(180deg, rgba(3,3,3,0.88) 0%, rgba(3,3,3,0.75) 50%, rgba(3,3,3,0.92) 100%)', opacity: 1 }} />
              <div className="absolute inset-0 transition-opacity duration-700 group-hover:opacity-40"
                style={{ background: 'rgba(3,3,3,0.55)' }} />
              <div className="absolute inset-0" style={{ background: c.accentColor }} />

              {/* Content */}
              <div className="relative h-full flex flex-col justify-between p-7 sm:p-8">
                <div>
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center mb-5 text-base"
                    style={{ background: 'rgba(168,143,94,0.12)', border: '1px solid rgba(168,143,94,0.2)' }}>
                    📊
                  </div>
                  <p className="text-white/40 text-xs font-light uppercase mb-3" style={{ letterSpacing: '0.12em' }}>{c.company}</p>
                  <p className="font-extralight mb-4 leading-tight" style={{ ...champagneText, fontSize: 'clamp(1.5rem, 3vw, 2.1rem)', letterSpacing: '-0.02em' }}>
                    {c.result}
                  </p>
                </div>
                <p className="text-white/60 text-sm leading-relaxed font-light group-hover:text-white/75 transition-colors duration-500">
                  {c.text}
                </p>
              </div>

              {/* Top accent line */}
              <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(212,193,151,0.35), transparent)' }} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ==================== PARALLAX QUOTE (NEW) ====================
const ParallaxQuoteSection: React.FC = () => {
  const { t } = useLanguage();
  const sectionRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start end', 'end start'] });

  const bgY = useTransform(scrollYProgress, [0, 1], ['-8%', '8%']);
  const textY = useTransform(scrollYProgress, [0, 1], ['6%', '-6%']);
  const bgYSpring = useSpring(bgY, { damping: 40, stiffness: 120 });
  const textYSpring = useSpring(textY, { damping: 40, stiffness: 100 });

  return (
    <section ref={sectionRef} className="relative overflow-hidden" style={{ minHeight: '60vh' }}>

      {/* Parallax BG */}
      <motion.div className="absolute inset-0 will-change-transform" style={{ y: bgYSpring }}>
        <div
          className="absolute inset-[-12%] bg-cover bg-center"
          style={{ backgroundImage: `url(${UNSPLASH.darkTech})` }} />
        <div className="absolute inset-0" style={{ background: 'rgba(3,3,3,0.82)' }} />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 50%, rgba(168,143,94,0.08) 0%, transparent 70%)' }} />
      </motion.div>

      {/* Top/Bottom fade */}
      <div className="absolute top-0 left-0 right-0 h-32 z-10" style={{ background: 'linear-gradient(180deg, #030303 0%, transparent 100%)' }} />
      <div className="absolute bottom-0 left-0 right-0 h-32 z-10" style={{ background: 'linear-gradient(0deg, #030303 0%, transparent 100%)' }} />

      {/* Content */}
      <motion.div style={{ y: textYSpring }} className="relative z-20 flex flex-col items-center justify-center min-h-[60vh] px-6 py-28 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: '-100px' }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}>

          {/* Decorative quote mark */}
          <div className="flex justify-center mb-8">
            <span className="font-extralight leading-none select-none" style={{ ...champagneText, fontSize: '7rem', opacity: 0.4, lineHeight: 1 }}>"</span>
          </div>

          <blockquote className="font-extralight text-white/90 max-w-4xl mx-auto leading-tight mb-8"
            style={{ fontSize: 'clamp(1.4rem, 4vw, 2.8rem)', letterSpacing: '-0.02em' }}>
            {t('quote.pullquote')}
          </blockquote>

          <GoldDivider />

          <p className="mt-5 font-light text-white/40 uppercase" style={{ fontSize: '11px', letterSpacing: '0.25em' }}>
            {t('quote.author')}
          </p>
          <p className="mt-3 font-light text-white/35 max-w-lg mx-auto" style={{ fontSize: '0.8rem', letterSpacing: '0.05em' }}>
            {t('quote.subtitle')}
          </p>
        </motion.div>
      </motion.div>

    </section>
  );
};

// ==================== INVEST ====================
const InvestSection: React.FC = () => {
  const { t, lang } = useLanguage();
  return (
    <section id="invest" className="py-20 sm:py-28 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #030303 0%, #070600 100%)' }}>
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(168,143,94,0.18), transparent)' }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }}
          className="text-center mb-14">
          <SectionLabel>{t('invest.title')}</SectionLabel>
          <GoldDivider />
        </motion.div>
        <div className="grid sm:grid-cols-2 gap-5 sm:gap-6">
          {[
            { title: t('invest.title'), text: t('invest.text'), items: [t(`invest.item1.${lang}`), t(`invest.item2.${lang}`), t(`invest.item3.${lang}`)], image: UNSPLASH.circuit },
            { title: t('crypto.title'), text: t('crypto.text'), items: [t(`crypto.item1.${lang}`), t(`crypto.item2.${lang}`), t(`crypto.item3.${lang}`)], image: UNSPLASH.aiAbstract },
          ].map((card, i) => (
            <motion.div key={i} variants={fadeUp} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-30px' }}
              className="relative p-7 sm:p-9 rounded-3xl overflow-hidden"
              style={{ border: '1px solid rgba(168,143,94,0.12)' }}>
              {/* Subtle bg image */}
              <div className="absolute inset-0 bg-cover bg-center opacity-10" style={{ backgroundImage: `url(${card.image})` }} />
              <div className="absolute inset-0" style={{ background: 'rgba(255,255,255,0.02)' }} />
              <div className="relative">
                <h3 className="font-extralight mb-3 uppercase leading-tight text-white/90" style={{ fontSize: 'clamp(1rem, 2vw, 1.2rem)', letterSpacing: '0.12em' }}>
                  {card.title}
                </h3>
                <p className="text-sm text-white/55 leading-relaxed mb-6 font-light">{card.text}</p>
                <ul className="space-y-2">
                  {card.items.map((item, j) => (
                    <li key={j} className="text-xs font-light text-white/55 uppercase" style={{ letterSpacing: '0.12em' }}>{item}</li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ==================== FAQ ====================
const FAQSection: React.FC = () => {
  const { t } = useLanguage();
  const [open, setOpen] = useState<number | null>(null);
  const faqs = [1, 2, 3, 4, 5].map(i => ({ q: t(`faq.${i}.q`), a: t(`faq.${i}.a`) }));
  return (
    <section className="py-20 sm:py-28 relative overflow-hidden" style={{ background: '#040404' }}>
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(168,143,94,0.18), transparent)' }} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }}
          className="text-center mb-14">
          <SectionLabel>{t('faq.title')}</SectionLabel>
          <h2 className="font-extralight text-white/90 leading-tight mb-3" style={{ fontSize: 'clamp(1.7rem, 5vw, 3rem)', letterSpacing: '-0.025em' }}>
            {t('faq.sub')}
          </h2>
          <GoldDivider />
        </motion.div>
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <motion.div key={i} variants={fadeUp} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-20px' }}
              className="rounded-2xl overflow-hidden"
              style={{ border: `1px solid ${open === i ? 'rgba(168,143,94,0.3)' : 'rgba(168,143,94,0.1)'}`, transition: 'border-color 0.3s' }}>
              <button
                className="w-full flex items-center justify-between px-6 py-5 text-left transition-colors"
                style={{ background: open === i ? 'rgba(168,143,94,0.06)' : 'rgba(255,255,255,0.02)' }}
                onClick={() => setOpen(open === i ? null : i)}>
                <span className="text-white/85 text-sm font-light pr-4 leading-snug">{faq.q}</span>
                <motion.span animate={{ rotate: open === i ? 45 : 0 }} transition={{ duration: 0.25 }}
                  className="flex-shrink-0 text-xl font-extralight" style={{ color: 'rgba(168,143,94,0.7)' }}>+</motion.span>
              </button>
              <AnimatePresence>
                {open === i && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: [0.25, 0, 0, 1] }}
                    style={{ overflow: 'hidden' }}>
                    <p className="px-6 pb-5 text-sm text-white/55 font-light leading-relaxed">{faq.a}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ==================== PARTNERS ====================
const PartnersSection: React.FC = () => {
  const { t } = useLanguage();
  return (
    <section id="partners" className="py-20 sm:py-28 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #030303 0%, #070600 100%)' }}>
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(168,143,94,0.2), transparent)' }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }}
          className="text-center mb-14">
          <SectionLabel>{t('partners.title')}</SectionLabel>
          <h2 className="font-extralight text-white/90 leading-tight mb-3" style={{ fontSize: 'clamp(1.7rem, 5vw, 3rem)', letterSpacing: '-0.025em' }}>
            {t('partners.subtitle')}
          </h2>
          <GoldDivider />
        </motion.div>
        <div className="flex justify-center">
          {partnersData.map((partner, index) => (
            <motion.div key={partner.id} variants={fadeUp} custom={index} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-40px' }}
              className="group relative rounded-3xl w-full max-w-sm overflow-hidden transition-all duration-400"
              style={{ background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(168,143,94,0.18)' }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(168,143,94,0.4)')}
              onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(168,143,94,0.18)')}>
              <div className="h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(168,143,94,0.5), transparent)' }} />
              <div className="relative h-28 sm:h-36 flex items-center justify-center" style={{ background: 'rgba(168,143,94,0.03)' }}>
                <div className="flex flex-col items-center gap-2">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl opacity-70 group-hover:opacity-90 transition-opacity duration-300"
                    style={{ background: 'rgba(168,143,94,0.1)', border: '1px solid rgba(168,143,94,0.18)' }}>⚖️</div>
                  <span className="uppercase font-light text-white/40" style={{ fontSize: '9px', letterSpacing: '0.2em' }}>{t('partners.general')}</span>
                </div>
              </div>
              <div className="p-6 sm:p-8">
                <h3 className="font-extralight text-white/90 mb-1" style={{ fontSize: 'clamp(1.3rem, 3vw, 1.6rem)', letterSpacing: '-0.01em' }}>{partner.name}</h3>
                <p className="text-white/40 text-xs mb-2 font-light">{partner.description}</p>
                <p className="text-white/30 text-xs mb-5 leading-relaxed font-light">{t('partners.legal')}</p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {[t('partners.tag1'), t('partners.tag2'), t('partners.tag3')].map((tag, i) => (
                    <span key={i} className="px-2.5 py-1 rounded-lg font-light"
                      style={{ background: 'rgba(168,143,94,0.06)', border: '1px solid rgba(168,143,94,0.12)', color: 'rgba(255,255,255,0.45)', fontSize: '10px', letterSpacing: '0.05em' }}>
                      {tag}
                    </span>
                  ))}
                </div>
                <a href={partner.url} target="_blank" rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full text-black font-medium py-3.5 rounded-xl transition-all text-sm min-h-[48px] active:scale-95 uppercase hover:opacity-90"
                  style={{ background: champagneGradient, letterSpacing: '0.12em' }}>
                  <span>{t('partners.visit')}</span>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ==================== CTA SECTION (PREMIUM REDESIGN) ====================
const CTASection: React.FC = () => {
  const { t } = useLanguage();
  const sectionRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start end', 'end start'] });
  const bgY = useTransform(scrollYProgress, [0, 1], ['-6%', '6%']);
  const bgYSpring = useSpring(bgY, { damping: 40, stiffness: 120 });

  return (
    <section ref={sectionRef} className="relative overflow-hidden py-0" style={{ background: '#040404' }}>
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(168,143,94,0.2), transparent)' }} />

      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 min-h-[680px]">

          {/* LEFT: BG image (parallax) */}
          <div className="relative overflow-hidden min-h-[300px] lg:min-h-full order-2 lg:order-1">
            <motion.div className="absolute inset-0 will-change-transform" style={{ y: bgYSpring }}>
              <div className="absolute inset-[-8%] bg-cover bg-center"
                style={{ backgroundImage: `url(${UNSPLASH.dubai})` }} />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(4,4,4,0.6) 0%, transparent 50%)' }} />
              <div className="absolute inset-0" style={{ background: 'linear-gradient(0deg, rgba(4,4,4,0.7) 0%, transparent 50%)' }} />
              <div className="absolute inset-0 opacity-20" style={{ background: 'radial-gradient(ellipse at 60% 50%, rgba(168,143,94,0.5), transparent 70%)' }} />
            </motion.div>
            {/* Floating stats */}
            <div className="absolute bottom-8 left-8 right-8 lg:right-auto flex flex-wrap gap-3">
              {[
                { val: '-30%', label: 'Cost Reduction' },
                { val: '48h', label: 'First Results' },
                { val: 'FREE', label: 'IT Audit' },
              ].map((s, i) => (
                <motion.div key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + i * 0.1, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                  className="flex flex-col px-4 py-3 rounded-xl"
                  style={{ background: 'rgba(3,3,3,0.8)', border: '1px solid rgba(168,143,94,0.2)', backdropFilter: 'blur(16px)' }}>
                  <span className="font-extralight" style={{ ...champagneText, fontSize: '1.3rem', letterSpacing: '-0.02em' }}>{s.val}</span>
                  <span className="text-[9px] text-white/40 uppercase mt-0.5 font-light" style={{ letterSpacing: '0.15em' }}>{s.label}</span>
                </motion.div>
              ))}
            </div>
          </div>

          {/* RIGHT: Form */}
          <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-14 py-20 order-1 lg:order-2">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }}>
              <SectionLabel>Free Consultation</SectionLabel>
              <h2 className="font-extralight text-white/90 leading-tight mb-4" style={{ fontSize: 'clamp(1.8rem, 4vw, 3rem)', letterSpacing: '-0.025em' }}>
                {t('cta.title')}
              </h2>
              <GoldDivider />
              <p className="text-white/50 leading-relaxed mt-4 font-light" style={{ fontSize: '0.9rem' }}>
                {t('cta.subtitle')}
              </p>
            </motion.div>

            <motion.form
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="mt-8 space-y-4"
              onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                formData.append("access_key", "79f179a3-2502-479e-9d5d-e923194757ea");
                formData.append("subject", "Новая заявка (Открытая форма на сайте)");
                try {
                  const response = await fetch("https://api.web3forms.com/submit", { method: "POST", body: formData });
                  if (response.ok) { alert('Заявка успешно отправлена!'); (e.target as HTMLFormElement).reset(); }
                  else { alert('Произошла ошибка при отправке.'); }
                } catch { alert('Ошибка сети.'); }
              }}>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="relative group/input">
                  <input type="text" name="name" required placeholder={t('cta.name')}
                    className="w-full px-5 py-4 rounded-xl outline-none text-sm text-white/85 placeholder-white/25 font-light transition-all bg-transparent"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(168,143,94,0.15)' }}
                    onFocus={e => (e.target.style.borderColor = 'rgba(168,143,94,0.55)')}
                    onBlur={e => (e.target.style.borderColor = 'rgba(168,143,94,0.15)')} />
                </div>
                <div className="relative">
                  <input type="tel" name="phone" required placeholder={t('cta.phone')}
                    className="w-full px-5 py-4 rounded-xl outline-none text-sm text-white/85 placeholder-white/25 font-light transition-all"
                    style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(168,143,94,0.15)' }}
                    onFocus={e => (e.target.style.borderColor = 'rgba(168,143,94,0.55)')}
                    onBlur={e => (e.target.style.borderColor = 'rgba(168,143,94,0.15)')} />
                </div>
              </div>

              <input type="email" name="email" required placeholder={t('cta.email')}
                className="w-full px-5 py-4 rounded-xl outline-none text-sm text-white/85 placeholder-white/25 font-light transition-all"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(168,143,94,0.15)' }}
                onFocus={e => (e.target.style.borderColor = 'rgba(168,143,94,0.55)')}
                onBlur={e => (e.target.style.borderColor = 'rgba(168,143,94,0.15)')} />

              <button type="submit"
                className="w-full text-black font-medium py-4 rounded-xl text-sm min-h-[56px] active:scale-[0.98] transition-all uppercase hover:opacity-90 relative overflow-hidden group/btn"
                style={{ background: champagneGradient, letterSpacing: '0.12em', boxShadow: '0 12px 40px rgba(168,143,94,0.3)' }}>
                <span className="relative z-10">{t('cta.button')}</span>
                <div className="absolute inset-0 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-500"
                  style={{ background: 'linear-gradient(135deg, #D4C197 0%, #A88F5E 50%, #D4C197 100%)' }} />
              </button>

              <p className="text-center text-white/35 text-xs font-light tracking-wide">
                {t('cta.guarantee')}
              </p>
            </motion.form>
          </div>
        </div>
      </div>
    </section>
  );
};

// ==================== CONTACTS ====================
const ContactsSection: React.FC = () => {
  const { t } = useLanguage();
  return (
    <section id="contacts" className="py-20 sm:py-28 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #030303 0%, #050400 100%)' }}>
      {/* Golden background glow */}
      <div className="absolute top-1/4 right-0 w-[900px] h-[900px] opacity-20 pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(255,215,0,0.5) 0%, transparent 70%)',
          filter: 'blur(140px)',
          animation: 'float 30s ease-in-out infinite'
        }} />
      
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(168,143,94,0.2), transparent)' }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-60px' }}
          className="text-center mb-14">
          <SectionLabel>{t('contacts.title')}</SectionLabel>
          <GoldDivider />
        </motion.div>
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="flex flex-col gap-4">
            {[
              { icon: '📍', label: t('contacts.address'), href: 'https://maps.google.com/maps?q=Dubai%20IFZA%20Business%20Park' },
              { icon: '📞', label: t('contacts.phone'), href: 'tel:+971561366651' },
              { icon: '✉️', label: t('contacts.email'), href: 'mailto:info@marqellon.com' },
            ].map((item, i) => (
              <motion.a key={i} href={item.href} target={i === 0 ? '_blank' : undefined} rel={i === 0 ? 'noreferrer' : undefined}
                variants={fadeUp} custom={i} initial="hidden" whileInView="visible" viewport={{ once: true, margin: '-20px' }}
                whileHover={{ scale: 1.1, borderColor: 'rgba(212,193,151,0.6)', backgroundColor: 'rgba(212,193,151,0.08)', boxShadow: '0 0 30px rgba(212,193,151,0.3), 0 0 60px rgba(168,143,94,0.15)' }}
                className="flex items-center p-8 rounded-3xl group transition-all duration-300 active:scale-90 relative overflow-hidden cursor-pointer"
                style={{ 
                  background: 'linear-gradient(135deg, rgba(212,193,151,0.08) 0%, rgba(168,143,94,0.03) 100%)',
                  border: '2px solid rgba(212,193,151,0.2)',
                  boxShadow: '0 0 20px rgba(168,143,94,0.15), 0 8px 32px rgba(0,0,0,0.2)'
                }}>
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{ background: 'radial-gradient(ellipse at left, rgba(212,193,151,0.25) 0%, transparent 70%)', animation: 'glow-pulse 3s ease-in-out infinite' }} />
                <span className="text-4xl mr-6 transition-all duration-300 group-hover:scale-150 group-hover:rotate-12 relative z-10 origin-center">{item.icon}</span>
                <span className="text-white/75 text-lg font-light leading-relaxed group-hover:text-white transition-colors duration-300 relative z-10">{item.label}</span>
              </motion.a>
            ))}
          </div>
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="w-full h-[350px] sm:h-[450px] rounded-3xl overflow-hidden relative"
            style={{ border: '1px solid rgba(168,143,94,0.15)' }}>
            <iframe
              title="Marqellon Office Location - Dubai IFZA Business Park"
              src="https://maps.google.com/maps?q=Dubai%20IFZA%20Business%20Park&t=&z=13&ie=UTF8&iwloc=&output=embed"
              width="100%" height="100%"
              style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg) contrast(85%) opacity(80%)' }}
              allowFullScreen={false} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ==================== MODAL ====================
const Modal: React.FC<{ isOpen: boolean; onClose: () => void }> = ({ isOpen, onClose }) => {
  const { t } = useLanguage();
  useLockScroll(isOpen);
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)', paddingLeft: 'env(safe-area-inset-left)', paddingRight: 'env(safe-area-inset-right)' }}
          onClick={onClose}>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0" style={{ background: 'rgba(3,3,3,0.95)', backdropFilter: 'blur(32px)' }} />
          <motion.div initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 34, stiffness: 260 }}
            className="relative w-full sm:max-w-md sm:rounded-[2rem] rounded-t-[2rem]"
            style={{ background: 'rgba(10,8,3,0.97)', border: '1px solid rgba(168,143,94,0.18)' }}
            onClick={e => e.stopPropagation()}>
            <div className="h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(168,143,94,0.6), transparent)' }} />
            <div className="flex justify-center pt-3 sm:hidden">
              <div className="w-10 h-1 rounded-full" style={{ background: 'rgba(168,143,94,0.25)' }} />
            </div>
            <div className="px-6 sm:px-8 pt-5 sm:pt-8 pb-7 sm:pb-10">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-extralight text-white/90" style={{ fontSize: 'clamp(1.1rem, 3vw, 1.5rem)', letterSpacing: '-0.01em' }}>{t('modal.title')}</h3>
                <button onClick={onClose}
                  className="w-9 h-9 flex items-center justify-center rounded-xl text-white/40 hover:text-white/70 text-xl leading-none transition-colors"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>×</button>
              </div>
              <p className="text-white/40 mb-6 text-xs font-light uppercase" style={{ letterSpacing: '0.12em' }}>{t('modal.subtitle')}</p>
              <form className="space-y-3"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const formData = new FormData(e.currentTarget);
                  formData.append("access_key", "79f179a3-2502-479e-9d5d-e923194757ea");
                  formData.append("subject", "Новая заявка (Модальное окно)");
                  try {
                    const response = await fetch("https://api.web3forms.com/submit", { method: "POST", body: formData });
                    if (response.ok) { alert('Заявка успешно отправлена!'); onClose(); }
                    else { alert('Произошла ошибка при отправке.'); }
                  } catch { alert('Ошибка сети.'); }
                }}>
                <motion.input type="text" name="name" required placeholder={t('modal.name')}
                  whileFocus={{ scale: 1.02, borderColor: 'rgba(168,143,94,0.5)' }}
                  className="w-full px-4 py-4 rounded-xl outline-none text-sm min-h-[52px] text-white/85 placeholder-white/25 font-light transition-all"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(168,143,94,0.15)', letterSpacing: '0.03em' }} />
                <motion.input type="tel" name="phone" required placeholder={t('modal.phone')}
                  whileFocus={{ scale: 1.02, borderColor: 'rgba(168,143,94,0.5)' }}
                  className="w-full px-4 py-4 rounded-xl outline-none text-sm min-h-[52px] text-white/85 placeholder-white/25 font-light transition-all"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(168,143,94,0.15)', letterSpacing: '0.03em' }} />
                <motion.input type="email" name="email" required placeholder={t('modal.email')}
                  whileFocus={{ scale: 1.02, borderColor: 'rgba(168,143,94,0.5)' }}
                  className="w-full px-4 py-4 rounded-xl outline-none text-sm min-h-[52px] text-white/85 placeholder-white/25 font-light transition-all"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(168,143,94,0.15)', letterSpacing: '0.03em' }} />
                <motion.button type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full text-black font-medium py-4 rounded-xl text-sm min-h-[52px] active:scale-95 transition-all mt-2 uppercase hover:opacity-90 relative overflow-hidden group"
                  style={{ background: champagneGradient, letterSpacing: '0.12em' }}>
                  <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{ background: 'radial-gradient(circle at center, rgba(255,255,255,0.2) 0%, transparent 70%)' }} />
                  <span className="relative">{t('modal.send')}</span>
                </motion.button>
              </form>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

// ==================== MAIN APP ====================
const AppInner: React.FC = () => {
  const { t } = useLanguage();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<string | null>(null);
  const onOpen = () => setIsModalOpen(true);

  return (
    <div className="min-h-screen" style={{ background: '#030303', color: '#fff', fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      <Header onOpen={onOpen} />
      <main>
        <Hero onOpen={onOpen} />
        <NumbersSection />
        <MissionSection />
        <WhySection />
        <ProcessSection />
        <ProductsSection onProductClick={setSelectedProduct} />
        <CasesSection onOpen={onOpen} />
        <ParallaxQuoteSection />
        <InvestSection />
        <FAQSection />
        <PartnersSection />
        <CTASection />
        <ContactsSection />
      </main>

      <footer className="py-16 px-4 text-center relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #020202 0%, #0a0805 100%)', borderTop: '2px solid rgba(212,193,151,0.15)' }}>
        {/* Animated top fade */}
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(212,193,151,0.3), transparent)', boxShadow: '0 0 30px rgba(212,193,151,0.2)' }} />
        {/* Glow orbs */}
        <div className="absolute -left-32 top-1/2 w-64 h-64 opacity-20 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(212,193,151,0.4) 0%, transparent 70%)', filter: 'blur(80px)', animation: 'float 15s ease-in-out infinite' }} />
        <div className="absolute -right-32 bottom-1/4 w-64 h-64 opacity-15 pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(168,143,94,0.3) 0%, transparent 70%)', filter: 'blur(80px)', animation: 'float 18s ease-in-out infinite reverse' }} />
        <div className="flex items-center justify-center gap-3 mb-6 relative z-10">
          <motion.img src={logo} alt="MARQELLON" className="h-10 object-contain opacity-70 group-hover:opacity-100 transition-opacity duration-300"
            whileHover={{ scale: 1.15 }}
            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        </div>
        <motion.div className="relative z-10" whileHover={{ textShadow: '0 0 20px rgba(212,193,151,0.6)' }}>
          <p className="font-light mb-2 uppercase" style={{ color: 'rgba(212,193,151,0.7)', fontSize: '10px', letterSpacing: '0.22em' }}>{t('footer.company')}</p>
          <p className="text-[10px] text-white/40 mb-3 font-light transition-colors hover:text-white/60">{t('footer.copyright')} · {t('footer.allrights')}</p>
          <motion.p className="text-[10px] font-light transition-colors duration-300" style={{ color: 'rgba(212,193,151,0.5)' }} whileHover={{ color: 'rgba(212,193,151,0.9)' }}>{t('footer.garcom')}</motion.p>
        </motion.div>
      </footer>

      {/* Mobile bottom bar */}
      <div className="fixed bottom-0 left-0 right-0 md:hidden z-[150]"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)', paddingLeft: 'env(safe-area-inset-left)', paddingRight: 'env(safe-area-inset-right)' }}>
        <div className="mx-3 mb-2 rounded-2xl p-2 flex gap-2"
          style={{ background: 'rgba(6,5,2,0.97)', border: '1px solid rgba(168,143,94,0.15)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', boxShadow: '0 -8px 40px rgba(0,0,0,0.7)' }}>
          <a href="tel:+971561366651"
            className="flex-1 flex items-center justify-center gap-2 rounded-xl py-3 active:opacity-70 transition-opacity min-h-[48px]"
            style={{ background: 'rgba(168,143,94,0.07)', border: '1px solid rgba(168,143,94,0.15)' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'rgba(212,193,151,0.8)' }}>
              <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 11a19.79 19.79 0 01-3.07-8.67A2 2 0 012 .18h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
            </svg>
            <span className="text-[11px] font-light text-white/65 uppercase" style={{ letterSpacing: '0.1em' }}>{t('mobile.call')}</span>
          </a>
          <a href="https://wa.me/971561366651" target="_blank" rel="noreferrer"
            className="flex-1 flex items-center justify-center gap-2 rounded-xl py-3 active:opacity-70 transition-opacity min-h-[48px]"
            style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.15)' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" style={{ color: 'rgba(74,222,128,0.75)' }} className="flex-shrink-0">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            <span className="text-[11px] font-light text-white/65 uppercase" style={{ letterSpacing: '0.1em' }}>{t('mobile.whatsapp')}</span>
          </a>
        </div>
      </div>

      {/* Desktop floating buttons */}
      <div className="hidden md:flex fixed right-5 top-1/2 -translate-y-1/2 flex-col gap-3 z-[150]">
        {[
          {
            href: 'tel:+971561366651', label: t('desktop.call'),
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'rgba(212,193,151,0.65)' }}>
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 11a19.79 19.79 0 01-3.07-8.67A2 2 0 012 .18h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
              </svg>
            )
          },
          {
            href: 'https://wa.me/971561366651', label: t('desktop.whatsapp'), target: '_blank',
            icon: (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" style={{ color: 'rgba(74,222,128,0.55)' }} className="flex-shrink-0">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
            )
          },
        ].map((btn, i) => (
          <a key={i} href={btn.href} target={(btn as any).target} rel={(btn as any).target ? 'noreferrer' : undefined}
            className="group flex items-center rounded-2xl overflow-hidden transition-all duration-400"
            style={{ padding: '12px', background: 'rgba(6,5,2,0.92)', border: '1px solid rgba(168,143,94,0.15)', backdropFilter: 'blur(20px)' }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.paddingRight = '18px'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(168,143,94,0.35)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.paddingRight = '12px'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(168,143,94,0.15)'; }}>
            {btn.icon}
            <span className="text-[10px] font-light text-white/50 uppercase whitespace-nowrap max-w-0 group-hover:max-w-[100px] overflow-hidden transition-all duration-400 ml-0 group-hover:ml-2.5" style={{ letterSpacing: '0.1em' }}>
              {btn.label}
            </span>
          </a>
        ))}
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
      <ProductModal isOpen={selectedProduct !== null} onClose={() => setSelectedProduct(null)} productKey={selectedProduct || ''} />
    </div>
  );
};

function App() {
  return (
    <LanguageProvider>
      <AppInner />
    </LanguageProvider>
  );
}

export default App;
